import React, { useState, useEffect } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import { Save, Mail, Users, Bell, Settings, Trash2, GitBranch, Plug, Image as ImageIcon, ToggleLeft, Plus, Library, Search, UserPlus, Lock, Unlock, Copy, X, LayoutGrid, Shield, FileText, Download, Upload, FileSignature, Maximize2, SlidersHorizontal, Activity, Sun } from 'lucide-react';
import APIService from '../services/api';
import { avecCle, useCleLecture } from '../services/cleLecture';
import { MatchingConfigSection } from './MatchingReglages';
import NotificationsRegles from './NotificationsRegles';
import { DiagnosticScoringSection } from './MatchingComparaison';
import BoiteCandidatures from './config/BoiteCandidatures';
import { CLES_MODULES } from './Marketplace';
import { useFeatures, loadFeatures, saveFeatures } from '../services/features';
import AgentToolsDebug from './AgentToolsDebug';
import ConsommationIA, { ROUTE_CONSO_IA } from './ConsommationIA';
import logoDefaut from '../assets/logo-defaut.png';
import CoutsEmployeur from './config/CoutsEmployeur';
import EntretiensConfig from './config/EntretiensConfig';

const BASE = 'https://besoins-tat-production.up.railway.app';

const SECTIONS = [
  { id: 'notifications', label: 'Notifications email',      icon: Mail },
  { id: 'users',         label: 'Utilisateurs & rôles',     icon: Users },
  { id: 'droits',        label: 'Rôles & droits',           icon: Shield },
  { id: 'templates',     label: 'Templates',                icon: FileText },
  { id: 'ia',            label: 'Intelligence Artificielle', icon: Settings },
  { id: 'commerciaux',   label: 'Contacts commerciaux',     icon: Users },
  { id: 'flow',          label: 'Flow de recrutement',      icon: GitBranch },
  { id: 'matching',      label: 'Algorithme de matching',   icon: SlidersHorizontal },
  { id: 'marque',        label: 'Logo',                     icon: ImageIcon },
  { id: 'docsign',       label: 'Signature électronique',   icon: FileSignature },
  { id: 'candidatures',  label: 'Boîte candidatures',       icon: Mail },
  { id: 'contrats',      label: 'Contrats',                 icon: FileText },
  { id: 'conges',        label: 'Types de congé',           icon: Sun },
  { id: 'couts',         label: 'Coûts employeur',          icon: Activity },
  { id: 'entretiens',    label: 'Entretiens',               icon: Sun },
  { id: 'rappels',       label: 'Rappels d\'entretien',       icon: Mail },
  { id: 'features',      label: 'Fonctionnalités',          icon: ToggleLeft },
  { id: 'referentiels',  label: 'Référentiels',             icon: Library },
  { id: 'boond',         label: 'Diagnostic Boond',         icon: Plug },
];

/**
 * Détourage du fond d'un tampon, dans le navigateur.
 *
 * Un tampon est rarement un PNG transparent : c'est une photo ou un scan de
 * tampon encré sur du papier. Posé tel quel sur un contrat, son rectangle de
 * papier recouvrirait le texte. On rend donc le fond transparent avant l'envoi.
 *
 * Le principe : l'encre est sombre, le papier est clair. On mesure la clarté du
 * papier sur le pourtour de l'image (c'est du fond, à coup sûr), on efface tout
 * ce qui est aussi clair, on garde tout ce qui est franchement sombre, et on
 * dégrade progressivement entre les deux — ce dégradé conserve les bords lissés
 * du tampon au lieu de les découper en escalier. L'image est enfin recadrée sur
 * l'encre, pour que la marge de papier photographiée ne compte pas dans la
 * largeur d'impression.
 *
 * `force` ajuste le seuil : négatif = plus doux (garde davantage), positif =
 * plus agressif (efface davantage). `zone` (en fractions de l'image, 0 à 1)
 * limite le traitement au cadre choisi à la main : sur la photo d'un courrier,
 * l'en-tête et le texte imprimé sont de l'encre eux aussi, et aucun réglage
 * automatique ne peut deviner que seul le tampon nous intéresse.
 */
async function chargerFichierImage(fichier) {
  const urlSrc = URL.createObjectURL(fichier);
  try {
    return await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i); i.onerror = () => rej(new Error('image illisible'));
      i.src = urlSrc;
    });
  } finally { URL.revokeObjectURL(urlSrc); }
}

/**
 * Qui dessine le sceau de signature, lu de façon tolérante.
 *
 * Le serveur peut porter l'information sous plusieurs formes selon la route
 * (`GET /config/docsign` ou `GET /config/docsign/rendu-sceau`) : une chaîne
 * directe, un objet, ou seulement une valeur par défaut. On accepte les trois
 * plutôt que de dépendre d'une seule orthographe : à défaut, l'écran afficherait
 * un réglage vide alors que le serveur a bien répondu.
 */
function lireRendu(j) {
  if (!j || typeof j !== 'object') return null;
  const src = j.rendu_sceau && typeof j.rendu_sceau === 'object' ? j.rendu_sceau : j;
  const brut = typeof j.rendu_sceau === 'string' ? j.rendu_sceau : (src.valeur || src.value || src.mode || null);
  const defaut = src.defaut || j.defaut || 'erpia';
  if (!brut && !src.defaut && !j.defaut) return null;   // la réponse ne parle pas du sceau
  return { valeur: brut || defaut, defaut };
}

async function lireRenduDedie() {
  try {
    const r = await fetch(`${BASE}/config/docsign/rendu-sceau`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
    if (!r.ok) return null;
    return lireRendu(await r.json().catch(() => null));
  } catch (e) { return null; }
}

/** Rectangle source à lire dans l'image, d'après la zone choisie. */
function cadreSource(img, zone) {
  if (!zone) return { sx: 0, sy: 0, sw: img.width, sh: img.height };
  return {
    sx: Math.round(zone.x * img.width),
    sy: Math.round(zone.y * img.height),
    sw: Math.max(1, Math.round(zone.w * img.width)),
    sh: Math.max(1, Math.round(zone.h * img.height)),
  };
}

/** Recadrage seul, sans toucher aux couleurs — pour l'option « image d'origine ». */
async function recadrerBrut(fichier, zone, maxCote = 1400) {
  const img = await chargerFichierImage(fichier);
  const { sx, sy, sw, sh } = cadreSource(img, zone);
  const ech = Math.min(1, maxCote / Math.max(sw, sh));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(sw * ech));
  c.height = Math.max(1, Math.round(sh * ech));
  c.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height);
  const blob = await new Promise(r => c.toBlob(r, 'image/png'));
  if (!blob) throw new Error('conversion impossible');
  return blob;
}

async function detourerFond(fichier, force = 0, maxCote = 1400, zone = null) {
  const img = await chargerFichierImage(fichier);
  const { sx, sy, sw, sh } = cadreSource(img, zone);
  const ech = Math.min(1, maxCote / Math.max(sw, sh));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(sw * ech));
  c.height = Math.max(1, Math.round(sh * ech));
  const ctx = c.getContext('2d');
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height);
  const donnees = ctx.getImageData(0, 0, c.width, c.height);
  const p = donnees.data;
  const clarte = (i) => 0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2];

  // Image déjà transparente (un vrai PNG détouré) : il n'y a pas de fond à
  // deviner, et le seuiller peindrait la transparence en noir. On se contente
  // alors de rogner la marge vide autour du tampon.
  let transparents = 0;
  for (let i = 3; i < p.length; i += 4) if (p[i] < 250) transparents++;
  const deja = transparents / (p.length / 4) > 0.02;

  // Clarté du papier : médiane du pourtour, insensible à une ombre locale.
  const bords = [];
  for (let x = 0; x < c.width; x++) {
    bords.push(clarte(x * 4));
    bords.push(clarte(((c.height - 1) * c.width + x) * 4));
  }
  for (let y = 0; y < c.height; y++) {
    bords.push(clarte(y * c.width * 4));
    bords.push(clarte((y * c.width + c.width - 1) * 4));
  }
  bords.sort((a, b) => a - b);
  const papier = bords.length ? bords[Math.floor(bords.length / 2)] : 255;

  const haut = Math.max(70, Math.min(252, papier - 12 - force));
  const bas = haut * 0.45;

  let minX = c.width, minY = c.height, maxX = -1, maxY = -1;
  for (let i = 0, px = 0; i < p.length; i += 4, px++) {
    let a;
    if (deja) {
      a = p[i + 3];
    } else {
      const L = clarte(i);
      a = L >= haut ? 0 : L <= bas ? 255 : Math.round((255 * (haut - L)) / (haut - bas));
      p[i + 3] = a;
    }
    if (a > 24) {
      const x = px % c.width, y = (px / c.width) | 0;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) throw new Error('aucune encre détectée');
  ctx.putImageData(donnees, 0, 0);

  // Recadrage sur l'encre, avec une marge de respiration.
  const marge = Math.round(Math.max(c.width, c.height) * 0.02);
  const rx = Math.max(0, minX - marge), ry = Math.max(0, minY - marge);
  const rw = Math.min(c.width - rx, maxX - minX + 1 + marge * 2);
  const rh = Math.min(c.height - ry, maxY - minY + 1 + marge * 2);
  const c2 = document.createElement('canvas');
  c2.width = rw; c2.height = rh;
  c2.getContext('2d').drawImage(c, rx, ry, rw, rh, 0, 0, rw, rh);
  const blob = await new Promise(r => c2.toBlob(r, 'image/png'));
  if (!blob) throw new Error('conversion impossible');
  return { blob, deja };
}

/**
 * Cadreur : on glisse sur la photo pour encadrer le tampon.
 *
 * Les coordonnées sont des fractions de l'image (0 à 1), jamais des pixels
 * d'écran : la même zone vaut donc pour la vignette et pour le plein écran, et
 * elle reste juste quelle que soit la taille d'affichage.
 */
function CadreurTampon({ url, zone, onZone, hauteur }) {
  const boite = React.useRef(null);
  const [trace, setTrace] = useState(null);

  const ratio = (e) => {
    const r = boite.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const debut = (e) => {
    e.preventDefault();
    const p = ratio(e);
    setTrace({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const bouge = (e) => { if (trace) { const p = ratio(e); setTrace(t => ({ ...t, x1: p.x, y1: p.y })); } };
  const fin = () => {
    if (!trace) return;
    const z = {
      x: Math.min(trace.x0, trace.x1), y: Math.min(trace.y0, trace.y1),
      w: Math.abs(trace.x1 - trace.x0), h: Math.abs(trace.y1 - trace.y0),
    };
    setTrace(null);
    // Un simple clic (zone minuscule) veut dire « toute la photo », pas une
    // sélection d'un pixel dont on ne pourrait rien tirer.
    onZone(z.w > 0.03 && z.h > 0.03 ? z : null);
  };

  const vue = trace
    ? { x: Math.min(trace.x0, trace.x1), y: Math.min(trace.y0, trace.y1), w: Math.abs(trace.x1 - trace.x0), h: Math.abs(trace.y1 - trace.y0) }
    : zone;

  return (
    <div ref={boite} onPointerDown={debut} onPointerMove={bouge} onPointerUp={fin} onPointerCancel={fin}
      style={{ position: 'relative', display: 'inline-block', lineHeight: 0, overflow: 'hidden', cursor: 'crosshair', touchAction: 'none', userSelect: 'none', borderRadius: '10px' }}>
      <img src={url} alt="Tampon à encadrer" draggable={false}
        style={{ display: 'block', maxHeight: hauteur, maxWidth: '100%', objectFit: 'contain' }} />
      {vue && vue.w > 0 && vue.h > 0 && (
        <div style={{
          position: 'absolute',
          left: `${vue.x * 100}%`, top: `${vue.y * 100}%`,
          width: `${vue.w * 100}%`, height: `${vue.h * 100}%`,
          border: '2px solid #6366f1',
          boxShadow: '0 0 0 9999px rgba(15,23,42,0.5)',
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

// ── Types de congé — catalogue de l'organisation ─────────────────────────────
// GET/POST /config/leave-types · PUT /config/leave-types/:code (admin).
// Pas de suppression : on DÉSACTIVE, pour que les demandes passées restent
// lisibles. Les ressources ne voient que les types actifs dans leurs demandes.
function TypesCongeSection({ showNotification }) {
  const [types, setTypes] = useState(null);      // null = chargement
  const [absent, setAbsent] = useState(false);   // route pas encore déployée
  const [libelle, setLibelle] = useState('');
  const [code, setCode] = useState('');
  const [codeTouche, setCodeTouche] = useState(false); // code édité à la main → on arrête de le déduire
  const [busy, setBusy] = useState(false);
  const [busyCode, setBusyCode] = useState(null);
  const [edition, setEdition] = useState(null);  // { code, valeur } → renommage en cours
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}` };

  // Le serveur attend un slug [a-z0-9_-]{1,30} : déduit du libellé, corrigeable.
  const slug = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9_-]+/g, '_').replace(/^[_-]+|[_-]+$/g, '').slice(0, 30);
  const codeFinal = slug(codeTouche ? code : libelle);

  const charger = async () => {
    try {
      const r = await fetch(`${BASE}/config/leave-types`, { headers });
      if (r.status === 404 || r.status === 405 || r.status === 501) { setAbsent(true); setTypes([]); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const arr = j.data || (Array.isArray(j) ? j : []);
      setAbsent(false);
      setTypes(Array.isArray(arr) ? arr : []);
    } catch (e) { setTypes([]); showNotification(`Types de congé illisibles : ${e.message}`, 'error'); }
  };
  useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ajouter = async () => {
    if (!libelle.trim() || !codeFinal) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/config/leave-types`, { method: 'POST', headers, body: JSON.stringify({ code: codeFinal, label: libelle.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 405 || r.status === 501) { showNotification('Le catalogue des types de congé arrive côté serveur — rien n\'a été enregistré.', 'error'); return; }
      // 409 (code déjà pris) et 400 : le message du serveur fait foi, tel quel.
      if (!r.ok || j.success === false) { showNotification(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification(j.message || `Type « ${libelle.trim()} » ajouté — il est proposé aux ressources dès maintenant.`, 'success');
      setLibelle(''); setCode(''); setCodeTouche(false);
      charger();
    } catch (e) { showNotification(`Ajout impossible : ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const modifier = async (c, patch, okMsg) => {
    setBusyCode(c);
    try {
      const r = await fetch(`${BASE}/config/leave-types/${encodeURIComponent(c)}`, { method: 'PUT', headers, body: JSON.stringify(patch) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification(j.message || okMsg, 'success');
      setEdition(null);
      charger();
    } catch (e) { showNotification(`Modification impossible : ${e.message}`, 'error'); }
    finally { setBusyCode(null); }
  };

  const desactiver = async (t) => {
    if (!await confirmErpia({ message: `Désactiver « ${t.label} » ?\n\nIl ne sera plus proposé aux ressources dans leurs demandes de congé. Les demandes déjà posées avec ce type restent lisibles, et le type peut être réactivé à tout moment.` })) return;
    modifier(t.code, { actif: false }, `« ${t.label} » désactivé.`);
  };

  const rangée = { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderTop: '1px solid var(--hair)' };

  return (
    <div>
      <h2 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontSize: '19px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Types de congé</h2>
      <p style={{ margin: '5px 0 16px', fontSize: '12.5px', color: 'var(--text-2)' }}>Les types proposés aux ressources dans leurs demandes de congé — et affichés dans leur calendrier CRA. Renommer un type s'applique partout ; désactiver un type ne supprime rien.</p>

      {absent && <p style={{ margin: '0 0 14px', padding: '10px 12px', borderRadius: '8px', background: 'var(--code-bg)', fontSize: '12.5px', color: 'var(--text-2)' }}>Le catalogue des types de congé arrive côté serveur — les ressources voient en attendant les types standards (Congés, Maladie, Autre).</p>}

      {types === null ? <p style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</p> : (<>
        {types.length === 0 && !absent && <p style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun type — ajoutez le premier ci-dessous.</p>}
        <div style={{ marginBottom: '16px' }}>
          {types.map((t, i) => (
            <div key={t.code || i} style={{ ...rangée, borderTop: i === 0 ? 'none' : rangée.borderTop, opacity: t.actif === false ? 0.62 : 1 }}>
              {edition?.code === t.code ? (
                <input className="erp-field" value={edition.valeur} autoFocus
                  onChange={e => setEdition({ code: t.code, valeur: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter' && edition.valeur.trim()) modifier(t.code, { label: edition.valeur.trim() }, 'Type renommé.'); if (e.key === 'Escape') setEdition(null); }}
                  style={{ flex: 1, minWidth: 0 }} aria-label={`Nouveau libellé pour ${t.label}`} />
              ) : (
                <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t.label}
                  {t.actif === false && <span style={{ marginLeft: '8px', fontSize: '10.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-3)' }}>désactivé</span>}
                </span>
              )}
              <span className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-3)', background: 'var(--code-bg)', padding: '2px 7px', borderRadius: '6px', flex: 'none' }}>{t.code}</span>
              {edition?.code === t.code ? (<>
                <button className="erp-btn erp-btn--primary erp-btn--sm" disabled={busyCode === t.code || !edition.valeur.trim()} onClick={() => modifier(t.code, { label: edition.valeur.trim() }, 'Type renommé.')}>Enregistrer</button>
                <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setEdition(null)}>Annuler</button>
              </>) : (<>
                <button className="erp-btn erp-btn--secondary erp-btn--sm" disabled={busyCode === t.code} onClick={() => setEdition({ code: t.code, valeur: t.label })}>Renommer</button>
                {t.actif === false
                  ? <button className="erp-btn erp-btn--secondary erp-btn--sm" disabled={busyCode === t.code} onClick={() => modifier(t.code, { actif: true }, `« ${t.label} » réactivé.`)}>Réactiver</button>
                  : <button className="erp-btn erp-btn--danger erp-btn--sm" disabled={busyCode === t.code} onClick={() => desactiver(t)}>Désactiver</button>}
              </>)}
            </div>
          ))}
        </div>

        <div style={{ paddingTop: '14px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '8px' }}>Ajouter un type</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="erp-field" value={libelle} onChange={e => setLibelle(e.target.value)} placeholder="Libellé — ex. Congé maternité"
              onKeyDown={e => { if (e.key === 'Enter') ajouter(); }} style={{ flex: '1 1 220px' }} aria-label="Libellé du nouveau type" />
            <input className="erp-field ats-mono" value={codeTouche ? code : codeFinal} onChange={e => { setCode(e.target.value); setCodeTouche(true); }} placeholder="code"
              title="Identifiant technique (a-z, 0-9, _ ou -) — déduit du libellé, corrigeable" style={{ width: '150px', fontSize: '12px' }} aria-label="Code du nouveau type" />
            <button className="erp-btn erp-btn--primary" disabled={busy || !libelle.trim() || !codeFinal} onClick={ajouter}>
              {busy ? 'Ajout…' : 'Ajouter le type'}
            </button>
          </div>
          <p style={{ margin: '7px 0 0', fontSize: '11.5px', color: 'var(--text-faint)' }}>Le code identifie le type dans les données et ne change plus après création ; le libellé, lui, reste modifiable.</p>
        </div>
      </>)}
    </div>
  );
}

export default function ConfigPage({ showNotification }) {
  const [section, setSection] = useState('notifications');
  const [saving, setSaving] = useState(false);

  // « Consommation IA » : l'entrée de menu n'existe que si le serveur répond
  // 200 à la sonde — 403/404/501 = rien à dessiner. C'est le serveur qui
  // décide qui voit cet écran ; aucune liste d'ayants droit côté front (le
  // bundle JS est public, un e-mail en dur n'y cacherait rien à personne).
  const [consoIA, setConsoIA] = useState(null);
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(ROUTE_CONSO_IA, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
        if (!r.ok) return; // 403 = pas pour cet utilisateur, 404/501 = pas encore côté serveur
        const j = await r.json().catch(() => null);
        const d = j?.data || j;
        if (!annule && d && (Array.isArray(d.lignes) || d.totaux_par_feature)) setConsoIA(d);
      } catch { /* réseau : on ne montre rien */ }
    })();
    return () => { annule = true; };
  }, []);

  // Logo des documents (org-scoped) — GET/POST/DELETE /config/logo
  const [logoVer, setLogoVer] = useState(0);
  // 404 sur /config/logo = organisation sans logo personnalisé (c'est le contrat).
  const [logoAbsent, setLogoAbsent] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = React.useRef(null);
  // Clé de lecture (jamais le jeton de session dans une adresse, 28.09). Pas
  // d'image tant qu'elle n'est pas en main : un premier refus serait pris pour
  // « pas de logo ».
  const cleL = useCleLecture();
  const logoSrc = cleL ? avecCle(`${BASE}/config/logo?v=${logoVer}`) : null;
  const uploadLogo = async (file) => {
    if (!file) return;
    if (/svg/i.test(file.type) || /\.svg$/i.test(file.name)) { showNotification('SVG non supporté — utilise un PNG ou JPEG.', 'error'); return; }
    if (file.size > 2 * 1024 * 1024) { showNotification('Fichier trop lourd (max 2 Mo).', 'error'); return; }
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${BASE}/config/logo`, { method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}` }, body: fd });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
      showNotification('Logo mis à jour', 'success'); setLogoAbsent(false); setLogoVer(v => v + 1);
    } catch (e) { showNotification(`Échec upload logo : ${e.message}`, 'error'); } finally { setLogoUploading(false); }
  };
  const resetLogo = async () => {
    setLogoUploading(true);
    try {
      const r = await fetch(`${BASE}/config/logo`, { method: 'DELETE', headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification('Logo réinitialisé (logo par défaut)', 'success'); setLogoAbsent(false); setLogoVer(v => v + 1);
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); } finally { setLogoUploading(false); }
  };

  // Tampon de l'entreprise pour la contresignature des contrats
  // GET /config/docsign → { tampon: { present, name, largeur_mm, updated_at } }
  // POST /config/docsign/tampon (multipart file + largeur_mm) · DELETE pour retirer
  const [tampon, setTampon] = useState(null);       // null = pas encore chargé
  const [tamponVer, setTamponVer] = useState(0);
  const [tamponBusy, setTamponBusy] = useState(false);
  const [tamponLargeur, setTamponLargeur] = useState('');
  const [docsignAbsent, setDocsignAbsent] = useState(false); // route pas encore servie
  const tamponFileRef = React.useRef(null);
  const tamponSrc = cleL ? avecCle(`${BASE}/config/docsign/tampon?v=${tamponVer}`) : null;

  // Qui dessine le sceau du candidat : l'application (rendu de référence) ou le
  // prestataire de signature (rendu non validé, donc présenté comme un essai).
  const [rendu, setRendu] = useState(null);   // { valeur, defaut } | null
  // Apparence du sceau : figée côté serveur. On affiche sa raison et les
  // réglages qui existent réellement, plutôt qu'un réglage qui n'agirait pas.
  const [sceauInfo, setSceauInfo] = useState(null);
  const [renduBusy, setRenduBusy] = useState(false);

  // ── Valeurs par défaut des contrats (GET/PUT /config/contrat-defauts) ──
  // Elles remplissent startDate et employeeType quand ni le positionnement ni
  // la saisie n'en portent. Posées ici, jamais écrites en dur dans un écran.
  const [ctrDef, setCtrDef] = useState(null);        // { date_debut, type_contrat } | null = chargement
  const [ctrDefBusy, setCtrDefBusy] = useState(false);
  const [ctrDefAbsent, setCtrDefAbsent] = useState(false);   // écriture non servie
  const [ctrDefPerdus, setCtrDefPerdus] = useState([]);      // champs non conservés

  const lireCtrDef = React.useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/contrat-defauts`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (!r.ok) { setCtrDef({}); return {}; }
      const j = await r.json().catch(() => ({}));
      const v = j.valeurs || j.data?.valeurs || {};
      const lu = {
        date_debut: v.date_debut || v.startDate || v.date_demarrage || '',
        type_contrat: v.type_contrat || v.employeeType || '',
        salaire: v.annualSalary || v.salaire_contrat || v.salary || '',
        adresse: v.address || v.adresse || '',
        civilite: v.civility || v.civilite || '',
      };
      setCtrDef(lu);
      return lu;
    } catch (e) { setCtrDef({}); return {}; }
  }, []);
  useEffect(() => { if (section === 'contrats') { setCtrDefPerdus([]); lireCtrDef(); } }, [section, lireCtrDef]);

  const enregistrerCtrDef = async () => {
    if (!ctrDef) return;
    setCtrDefBusy(true); setCtrDefPerdus([]);
    try {
      const valeurs = {};
      if (String(ctrDef.date_debut || '').trim()) valeurs.date_debut = ctrDef.date_debut.trim();
      if (String(ctrDef.type_contrat || '').trim()) valeurs.type_contrat = ctrDef.type_contrat.trim();
      // Pas de salaire par défaut (04.11 : « le salaire est dynamique, en
      // fonction du TJM ») : il vient du TJM du lot. Un ancien défaut est effacé.
      if (String(ctrDef.salaire || '').trim()) valeurs.annualSalary = '';
      // Adresse et civilité : le contrat les prend sur la fiche ; à défaut, ici.
      if (String(ctrDef.adresse || '').trim()) valeurs.address = ctrDef.adresse.trim();
      if (String(ctrDef.civilite || '').trim()) valeurs.civility = ctrDef.civilite.trim();
      const r = await fetch(`${BASE}/config/contrat-defauts`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ valeurs }),
      });
      if (r.status === 404 || r.status === 405 || r.status === 501) { setCtrDefAbsent(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // Preuve avant annonce : on relit et on compare. Un défaut « enregistré »
      // qui ne l'est pas finirait en champ vide sur un contrat signé.
      const relu = await lireCtrDef();
      const perdus = [];
      if (valeurs.date_debut && String(relu.date_debut || '').slice(0, 10) !== valeurs.date_debut.slice(0, 10)) perdus.push('Date de début');
      if (valeurs.type_contrat && String(relu.type_contrat || '').trim() !== valeurs.type_contrat) perdus.push('Type d\'emploi');
      if (valeurs.address && String(relu.adresse || '').trim() !== valeurs.address) perdus.push('Adresse');
      if (valeurs.civility && String(relu.civilite || '').trim() !== valeurs.civility) perdus.push('Civilité');
      setCtrDefPerdus(perdus);
      if (!perdus.length) showNotification('Valeurs par défaut enregistrées', 'success');
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); }
    finally { setCtrDefBusy(false); }
  };

  // ── Récapitulatif client (GET/PUT /config/recap-societe) ──
  // Le bloc société écrit dans le classeur Excel du client, et les deux règles
  // de remplissage. Les noms des champs société sont ceux que le rapport de
  // remplissage réclame dans societe.a_completer.
  const CHAMPS_SOCIETE = [
    ['adresse', 'Adresse'], ['npa_ville', 'NPA et ville'], ['contact', 'Contact'],
    ['email', 'Email'], ['lse_cantonale', 'LSE cantonale'], ['lse_federale', 'LSE fédérale'],
  ];
  const [recap, setRecap] = useState(null);          // { societe, taux_defaut, tarif_defaut } | null
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapAbsent, setRecapAbsent] = useState(false);
  const [recapErreur, setRecapErreur] = useState(null);  // 400 du serveur, affiché tel quel

  const lireRecap = React.useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/recap-societe`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (r.status === 404 || r.status === 501) { setRecapAbsent(true); setRecap({}); return {}; }
      if (!r.ok) { setRecap({}); return {}; }
      const j = await r.json().catch(() => ({}));
      const d = j.data || j;
      // Forme verrouillée côté serveur : les clés vivent à la racine, jamais
      // sous « valeurs » — le repli a été retiré à sa demande.
      const lu = {
        societe: d.societe || {},
        taux_defaut: d.taux_defaut ?? '',
        tarif_defaut: d.tarif_defaut ?? 'aucun',
        // Absent de la réponse = serveur qui ne connaît pas encore la règle :
        // le champ n'est alors ni montré ni envoyé (il serait refusé).
        ...(d.permis_defaut !== undefined ? { permis_defaut: d.permis_defaut || '' } : {}),
      };
      setRecap(lu);
      return lu;
    } catch (e) { setRecap({}); return {}; }
  }, []);
  useEffect(() => { if (section === 'contrats') { setRecapErreur(null); lireRecap(); } }, [section, lireRecap]);

  const enregistrerRecap = async () => {
    if (!recap) return;
    setRecapBusy(true); setRecapErreur(null);
    try {
      const societe = {};
      CHAMPS_SOCIETE.forEach(([k]) => { const v = String(recap.societe?.[k] || '').trim(); if (v) societe[k] = v; });
      const corps = {
        societe,
        ...(String(recap.taux_defaut ?? '').trim() !== '' ? { taux_defaut: Number(recap.taux_defaut) } : {}),
        tarif_defaut: recap.tarif_defaut || 'aucun',
        ...(recap.permis_defaut !== undefined ? { permis_defaut: recap.permis_defaut || '' } : {}),
      };
      const r = await fetch(`${BASE}/config/recap-societe`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(corps),
      });
      if (r.status === 404 || r.status === 405 || r.status === 501) { setRecapAbsent(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) {
        const details = [
          Array.isArray(j.cles_inconnues) && j.cles_inconnues.length ? `Clés refusées : ${j.cles_inconnues.join(', ')}` : null,
          Array.isArray(j.cles_acceptees) && j.cles_acceptees.length ? `Clés acceptées : ${j.cles_acceptees.join(', ')}` : null,
        ].filter(Boolean).join(' · ');
        setRecapErreur([j.error || `HTTP ${r.status}`, details].filter(Boolean).join(' — '));
        return;
      }
      // Preuve avant annonce, comme partout : relire, comparer la règle tarif —
      // c'est elle qui décide si les cellules du classeur seront remplies.
      const relu = await lireRecap();
      if ((relu.tarif_defaut || 'aucun') !== (corps.tarif_defaut || 'aucun')) {
        setRecapErreur('Le réglage du tarif n\'a pas été conservé par le serveur — vérifiez avec l\'assistant.');
        return;
      }
      showNotification('Réglages du récapitulatif enregistrés', 'success');
    } catch (e) { setRecapErreur(e.message || 'Erreur réseau'); }
    finally { setRecapBusy(false); }
  };

  const chargerDocsign = React.useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/docsign`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (r.status === 404 || r.status === 501) { setDocsignAbsent(true); setTampon({ present: false }); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json().catch(() => ({}));
      setDocsignAbsent(false);
      setTampon(j.tampon || { present: false });
      setTamponLargeur(j.tampon?.largeur_mm ? String(j.tampon.largeur_mm) : '');
      setRendu(lireRendu(j) || (await lireRenduDedie()));
      setSceauInfo(j.sceau || null);
    } catch (e) { setTampon({ present: false }); }
  }, []);
  useEffect(() => { if (section === 'docsign') chargerDocsign(); }, [section, chargerDocsign]);

  // Image choisie, pas encore envoyée : on la détoure et on montre le résultat
  // avant d'écrire quoi que ce soit côté serveur.
  const [brouillon, setBrouillon] = useState(null);      // { fichier, urlOrigine }
  const [detourage, setDetourage] = useState(null);      // { blob, url } | 'echec' | null
  const [detourActif, setDetourActif] = useState(true);
  const [force, setForce] = useState(0);                 // réglage du détourage
  const [detourEnCours, setDetourEnCours] = useState(false);
  const [zone, setZone] = useState(null);                // cadre choisi, en fractions
  const [brut, setBrut] = useState(null);                // { blob, url } recadré, sans détourage
  const [plein, setPlein] = useState(false);             // photo en plein écran

  const choisirFichier = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg)$/i.test(file.type || '')) { showNotification('Format non supporté — utilisez un PNG ou un JPEG (une photo du tampon convient).', 'error'); return; }
    if (file.size > 8 * 1024 * 1024) { showNotification('Image trop lourde (max 8 Mo).', 'error'); return; }
    setBrouillon(b => { if (b?.urlOrigine) URL.revokeObjectURL(b.urlOrigine); return { fichier: file, urlOrigine: URL.createObjectURL(file) }; });
    setDetourActif(true);
    setForce(0);
    setZone(null);
  };

  const annulerBrouillon = () => {
    setBrouillon(b => { if (b?.urlOrigine) URL.revokeObjectURL(b.urlOrigine); return null; });
    setZone(null);
    setPlein(false);
  };

  useEffect(() => {
    if (!brouillon) { setDetourage(null); return; }
    let annule = false, urlCreee = null;
    setDetourEnCours(true);
    (async () => {
      try {
        let res = await detourerFond(brouillon.fichier, force, 1400, zone);
        // Le PNG produit doit rester sous la limite du serveur.
        if (res.blob.size > 2 * 1024 * 1024) res = await detourerFond(brouillon.fichier, force, 900, zone);
        if (annule) return;
        urlCreee = URL.createObjectURL(res.blob);
        setDetourage({ blob: res.blob, url: urlCreee, deja: res.deja });
      } catch (e) { if (!annule) setDetourage('echec'); }
      finally { if (!annule) setDetourEnCours(false); }
    })();
    return () => { annule = true; if (urlCreee) URL.revokeObjectURL(urlCreee); };
  }, [brouillon, force, zone]);

  useEffect(() => {
    if (!plein) return;
    const auClavier = (e) => { if (e.key === 'Escape') setPlein(false); };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [plein]);

  // Version « image d'origine » : recadrée elle aussi. Si l'utilisateur a
  // encadré le tampon, envoyer la photo entière serait le contraire de ce
  // qu'il vient de demander.
  useEffect(() => {
    if (!brouillon || !zone) { setBrut(null); return; }
    let annule = false, urlCreee = null;
    (async () => {
      try {
        let blob = await recadrerBrut(brouillon.fichier, zone, 1400);
        if (blob.size > 2 * 1024 * 1024) blob = await recadrerBrut(brouillon.fichier, zone, 900);
        if (annule) return;
        urlCreee = URL.createObjectURL(blob);
        setBrut({ blob, url: urlCreee });
      } catch (e) { if (!annule) setBrut(null); }
    })();
    return () => { annule = true; if (urlCreee) URL.revokeObjectURL(urlCreee); };
  }, [brouillon, zone]);

  const uploadTampon = async (file) => {
    if (!file) return false;
    if (file.size > 2 * 1024 * 1024) { showNotification('Fichier trop lourd (max 2 Mo) — réduisez la taille de l’image.', 'error'); return false; }
    const largeur = tamponLargeur.trim() === '' ? null : Number(tamponLargeur);
    if (largeur !== null && (!Number.isFinite(largeur) || largeur < 15 || largeur > 90)) {
      showNotification('La largeur doit être comprise entre 15 et 90 mm.', 'error'); return false;
    }
    setTamponBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (largeur !== null) fd.append('largeur_mm', String(largeur));
      const r = await fetch(`${BASE}/config/docsign/tampon`, { method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}` }, body: fd });
      if (r.status === 404 || r.status === 501) { setDocsignAbsent(true); showNotification('Le tampon arrive côté serveur — rien n’a été enregistré.', 'info'); return false; }
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
      showNotification('Tampon enregistré', 'success');
      setTamponVer(v => v + 1);
      await chargerDocsign();
      return true;
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); return false; }
    finally { setTamponBusy(false); }
  };

  const enregistrerTampon = async () => {
    if (!brouillon) return;
    const detoure = detourActif && detourage && detourage !== 'echec';
    const base = brouillon.fichier.name.replace(/\.[^.]+$/, '') || 'tampon';
    const blob = detoure ? detourage.blob : (brut ? brut.blob : null);
    const fichier = blob
      ? new File([blob], `${base}.png`, { type: 'image/png' })
      : brouillon.fichier;
    if (await uploadTampon(fichier)) annulerBrouillon();
  };

  const definirRendu = async (valeur) => {
    if (renduBusy || rendu?.valeur === valeur) return;
    setRenduBusy(true);
    try {
      const r = await fetch(`${BASE}/config/docsign/rendu-sceau`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rendu_sceau: valeur }),
      });
      if (r.status === 404 || r.status === 405 || r.status === 501) {
        showNotification('Ce réglage arrive côté serveur — rien n’a été modifié.', 'info');
        return;
      }
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
      showNotification(valeur === 'erpia' ? 'Sceau dessiné par l’application' : 'Sceau dessiné par le prestataire (essai)', 'success');
      // L'écran n'affiche que ce que le serveur renvoie ensuite : pas de
      // sélection déplacée en local avant confirmation.
      await chargerDocsign();
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); }
    finally { setRenduBusy(false); }
  };

  const supprimerTampon = async () => {
    if (!await confirmErpia({ message: 'Retirer le tampon ? Les contrats générés ensuite ne pourront plus l’apposer.' })) return;
    setTamponBusy(true);
    try {
      const r = await fetch(`${BASE}/config/docsign/tampon`, { method: 'DELETE', headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (!r.ok && r.status !== 404) throw new Error(`HTTP ${r.status}`);
      showNotification('Tampon retiré', 'success');
      setTamponVer(v => v + 1);
      await chargerDocsign();
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); } finally { setTamponBusy(false); }
  };

  // Feature flags de l'organisation (GET/PUT /config/features)
  const features = useFeatures();
  const [flagSaving, setFlagSaving] = useState(null); // clé en cours d'enregistrement
  const [newFlagKey, setNewFlagKey] = useState('');
  useEffect(() => { if (section === 'features') loadFeatures(); }, [section]);
  const toggleFlag = async (key, value) => {
    setFlagSaving(key);
    try { await saveFeatures({ [key]: value }); showNotification(`« ${features?.[key]?.label || key} » ${value ? 'activée' : 'désactivée'}`, 'success'); }
    catch (e) { showNotification(`Échec : ${e.message}`, 'error'); }
    finally { setFlagSaving(null); }
  };
  const addCustomFlag = async () => {
    const key = newFlagKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (!key) return;
    setFlagSaving(key);
    try { await saveFeatures({ [key]: true }); setNewFlagKey(''); showNotification(`Flag « ${key} » créé`, 'success'); }
    catch (e) { showNotification(`Échec : ${e.message}`, 'error'); }
    finally { setFlagSaving(null); }
  };

  // Catalogue de certifications (base de connaissances des anomalies CV-prep)
  // GET /cv-prep/certs · PUT /cv-prep/certs { nom, versions[], notes }
  const [certs, setCerts] = useState(null);
  const [certForm, setCertForm] = useState({ nom: '', versions: '', notes: '' });
  const [certSaving, setCertSaving] = useState(false);
  const [certQuery, setCertQuery] = useState('');
  const loadCerts = React.useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/cv-prep/certs`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      const j = await r.json().catch(() => ([]));
      setCerts(j.data || (Array.isArray(j) ? j : []));
    } catch { setCerts([]); }
  }, []);
  useEffect(() => { if (section === 'referentiels' && certs == null) loadCerts(); }, [section, certs, loadCerts]);
  const certsFiltered = React.useMemo(() => {
    const q = certQuery.trim().toLowerCase();
    if (!q || !Array.isArray(certs)) return certs || [];
    return certs.filter(c => `${c.nom || ''} ${(c.versions || []).join(' ')} ${c.notes || ''}`.toLowerCase().includes(q));
  }, [certs, certQuery]);
  const saveCert = async () => {
    const nom = certForm.nom.trim();
    if (!nom) { showNotification('Nom de certification requis.', 'error'); return; }
    setCertSaving(true);
    try {
      const versions = certForm.versions.split(',').map(s => s.trim()).filter(Boolean);
      const r = await fetch(`${BASE}/cv-prep/certs`, { method: 'PUT', headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ nom, versions, notes: certForm.notes.trim() }) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
      showNotification('Certification enregistrée', 'success'); setCertForm({ nom: '', versions: '', notes: '' }); loadCerts();
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); } finally { setCertSaving(false); }
  };

  // Notifications
  const [notifP1P2, setNotifP1P2]                 = useState('');
  const [notifNouveauCandidat, setNotifNouveauCandidat] = useState('');
  const [notifRapport, setNotifRapport]             = useState('');
  const [resendKey, setResendKey]                   = useState('');
  const [notifInappMax, setNotifInappMax]           = useState('10'); // cloche in-app : N dernières affichées & conservées

  // IA / Leman
  const [lemanEnabled, setLemanEnabled] = useState(false);
  const [lemanUrl, setLemanUrl]         = useState('');
  const [lemanKey, setLemanKey]         = useState('');
  const [lemanModel, setLemanModel]     = useState('');
  const [audioRetention, setAudioRetention] = useState('30');
  const [deletingAudio, setDeletingAudio]   = useState(false);

  // Backfill profils CV (rattrapage historique)
  const [backfill, setBackfill] = useState(null); // { running, total, done, ok, failed, skipped, errors }
  const [backfillStarting, setBackfillStarting] = useState(false);
  const backfillPoll = React.useRef({ active: false });
  // Backfill texte des CV (extraction texte brut, sans IA)
  const [cvText, setCvText] = useState(null); // { running, total, done, ok, failed, skipped, errors }
  const [cvTextStarting, setCvTextStarting] = useState(false);
  const cvTextPoll = React.useRef({ active: false });

  // Import documents Boond (asynchrone + poll)
  const [boondImport, setBoondImport] = useState(null); // { running, total, done, resumes_ok, files_ok, failed, skipped, errors }
  const [boondImportStarting, setBoondImportStarting] = useState(false);
  const [boondImportLimit, setBoondImportLimit] = useState('10'); // limite de test (Diagnostic Boond)
  const boondImportPoll = React.useRef({ active: false });

  // Diagnostic Boond (lecture seule)
  const [boondPing, setBoondPing] = useState(null); // { ok, sample } | { error }
  const [boondPinging, setBoondPinging] = useState(false);
  const [boondRef, setBoondRef] = useState('');
  const [boondProbe, setBoondProbe] = useState(null); // réponse brute
  const [boondProbing, setBoondProbing] = useState(false);
  const [boondList, setBoondList] = useState(null); // réponse brute de probe-list
  const [boondListing, setBoondListing] = useState(false);
  const [boondListRaw, setBoondListRaw] = useState(false); // afficher le JSON brut

  // Commerciaux
  const [commerciaux, setCommerciaux]     = useState([]);
  const [savingCommercial, setSavingCommercial] = useState(false);
  const [newCommercial, setNewCommercial] = useState({
    name: '', title: 'Responsable Commercial', email: '', phone_office: '', phone_mobile: ''
  });

  // Users
  const [users, setUsers] = useState([]);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` };

  useEffect(() => { fetchConfig(); fetchUsers(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BASE}/config`, { headers });
      const data = await res.json();
      setNotifP1P2((data.notif_p1p2?.emails || []).join(', '));
      setNotifNouveauCandidat((data.notif_candidat?.emails || []).join(', '));
      setNotifRapport((data.notif_rapport?.emails || []).join(', '));
      setResendKey(data.resend_key?.key || '');
      setNotifInappMax(String(data.notif_inapp?.max ?? 10));
      setLemanEnabled(data.leman_config?.enabled || false);
      setLemanUrl(data.leman_config?.url || '');
      setLemanKey(data.leman_config?.api_key || '');
      setLemanModel(data.leman_config?.model || '');
      setAudioRetention(String(data.audio_config?.retention_days ?? '30'));
      setCommerciaux(data.commerciaux?.list || []);
    } catch (e) { console.error(e); }
  };

  // Liste tolérante : le endpoint peut renvoyer un tableau nu, { data }, { users }…
  // et on se rabat sur GET /users (celui qui alimente déjà le menu « responsable »)
  // si /config/users/list est vide ou indisponible.
  const pickUsers = (j) => (Array.isArray(j) ? j : (j?.data || j?.users || j?.list || []));
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BASE}/config/users/list`, { headers });
      const j1 = res.ok ? await res.json().catch(() => null) : null;
      if (Array.isArray(j1?.roles_admis)) setRolesAdmis(j1.roles_admis);
      const arr = pickUsers(j1);
      if (arr.length) { setUsers(arr); return; }
      // repli
      const r2 = await fetch(`${BASE}/users`, { headers });
      const j2 = r2.ok ? await r2.json().catch(() => null) : null;
      if (Array.isArray(j2?.roles_admis)) setRolesAdmis(j2.roles_admis);
      setUsers(pickUsers(j2));
    } catch (e) { console.error(e); setUsers([]); }
  };

  const parseEmails = (str) => str.split(',').map(e => e.trim()).filter(Boolean);

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await Promise.all([
        fetch(`${BASE}/config/notif_p1p2`,    { method: 'PUT', headers, body: JSON.stringify({ value: { emails: parseEmails(notifP1P2) } }) }),
        fetch(`${BASE}/config/notif_candidat`,{ method: 'PUT', headers, body: JSON.stringify({ value: { emails: parseEmails(notifNouveauCandidat) } }) }),
        fetch(`${BASE}/config/notif_rapport`, { method: 'PUT', headers, body: JSON.stringify({ value: { emails: parseEmails(notifRapport) } }) }),
        fetch(`${BASE}/config/resend_key`,    { method: 'PUT', headers, body: JSON.stringify({ value: { key: resendKey } }) }),
        fetch(`${BASE}/config/notif_inapp`,   { method: 'PUT', headers, body: JSON.stringify({ value: { max: Math.max(1, parseInt(notifInappMax, 10) || 10) } }) }),
      ]);
      showNotification('Configuration sauvegardée', 'success');
    } catch (e) { showNotification('Erreur sauvegarde', 'error'); }
    setSaving(false);
  };

  const saveIA = async () => {
    setSaving(true);
    try {
      await fetch(`${BASE}/config/leman_config`, { method: 'PUT', headers, body: JSON.stringify({ value: { enabled: lemanEnabled, url: lemanUrl, api_key: lemanKey, model: lemanModel } }) });
      await fetch(`${BASE}/config/audio_config`, { method: 'PUT', headers, body: JSON.stringify({ value: { retention_days: parseInt(audioRetention) } }) });
      showNotification('Configuration IA sauvegardée', 'success');
    } catch (e) { showNotification('Erreur sauvegarde', 'error'); }
    setSaving(false);
  };

  const deleteAllAudio = async () => {
    if (!await confirmErpia({ message: 'Supprimer TOUS les enregistrements audio ? Irréversible.' })) return;
    setDeletingAudio(true);
    try {
      const res = await fetch(`${BASE}/calls/audio/delete-all`, { method: 'DELETE', headers });
      const data = await res.json();
      showNotification(`${data.deleted || 0} enregistrement(s) supprimé(s)`, 'success');
    } catch (e) { showNotification('Erreur suppression', 'error'); }
    setDeletingAudio(false);
  };

  // ── Backfill profils CV : lance le job puis poll le statut ──
  const pollBackfill = async () => {
    try {
      const res = await fetch(`${BASE}/candidates/backfill-profiles/status`, { headers });
      const data = await res.json();
      setBackfill(data);
      if (data.running && backfillPoll.current.active) {
        setTimeout(pollBackfill, 2000);
      } else {
        backfillPoll.current.active = false;
        if (!data.running) showNotification(`Rattrapage terminé : ${data.ok ?? 0} rempli(s) · ${data.failed ?? 0} échec(s) · ${data.skipped ?? 0} sans CV`, 'success');
      }
    } catch (e) { backfillPoll.current.active = false; showNotification('Erreur suivi du rattrapage', 'error'); }
  };
  const startBackfill = async () => {
    setBackfillStarting(true);
    try {
      const res = await fetch(`${BASE}/candidates/backfill-profiles`, { method: 'POST', headers, body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.error || `HTTP ${res.status}`);
      backfillPoll.current.active = true;
      pollBackfill();
    } catch (e) { showNotification('Erreur lancement : ' + e.message, 'error'); }
    finally { setBackfillStarting(false); }
  };
  // Reprend le suivi si un job tourne déjà à l'ouverture de la section IA
  useEffect(() => {
    if (section !== 'ia' || backfillPoll.current.active) return;
    fetch(`${BASE}/candidates/backfill-profiles/status`, { headers })
      .then(r => r.json())
      .then(d => { setBackfill(d); if (d.running) { backfillPoll.current.active = true; pollBackfill(); } })
      .catch(() => {});
    return () => { backfillPoll.current.active = false; };
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Backfill texte des CV (extraction texte brut, sans IA) ──
  const pollCvText = async () => {
    try {
      const res = await fetch(`${BASE}/candidates/backfill-cv-text/status`, { headers });
      const data = await res.json();
      setCvText(data);
      if (data.running && cvTextPoll.current.active) {
        setTimeout(pollCvText, 2000);
      } else {
        cvTextPoll.current.active = false;
        if (!data.running) showNotification(`Extraction texte terminée : ${data.ok ?? 0} extrait(s) · ${data.failed ?? 0} échec(s) · ${data.skipped ?? 0} ignoré(s)`, 'success');
      }
    } catch (e) { cvTextPoll.current.active = false; showNotification('Erreur suivi extraction texte', 'error'); }
  };
  const startCvText = async () => {
    setCvTextStarting(true);
    try {
      const res = await fetch(`${BASE}/candidates/backfill-cv-text`, { method: 'POST', headers, body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.error || `HTTP ${res.status}`);
      cvTextPoll.current.active = true;
      pollCvText();
    } catch (e) { showNotification('Erreur lancement : ' + e.message, 'error'); }
    finally { setCvTextStarting(false); }
  };
  useEffect(() => {
    if (section !== 'ia' || cvTextPoll.current.active) return;
    fetch(`${BASE}/candidates/backfill-cv-text/status`, { headers })
      .then(r => r.json())
      .then(d => { setCvText(d); if (d.running) { cvTextPoll.current.active = true; pollCvText(); } })
      .catch(() => {});
    return () => { cvTextPoll.current.active = false; };
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Import des candidats Boond (synchro complète : candidats + docs) : lance le job puis poll le statut ──
  const pollBoondImport = async () => {
    try {
      const res = await fetch(`${BASE}/candidats/boond/import-candidates/status`, { headers });
      const data = await res.json();
      setBoondImport(data);
      if (data.running && boondImportPoll.current.active) {
        setTimeout(pollBoondImport, 2000);
      } else {
        boondImportPoll.current.active = false;
        if (!data.running) showNotification(`Import Boond terminé : ${data.created ?? 0} créé(s) · ${data.updated ?? 0} mis à jour · ${data.resumes_ok ?? 0} CV · ${data.files_ok ?? 0} pièce(s) · ${data.activities_ok ?? 0} activité(s) · ${data.failed ?? 0} échec(s)`, 'success');
      }
    } catch (e) { boondImportPoll.current.active = false; showNotification('Erreur suivi de l\'import Boond', 'error'); }
  };
  const startBoondImport = async (limit) => {
    setBoondImportStarting(true);
    try {
      const body = (limit != null && limit > 0) ? { limit } : {};
      const res = await fetch(`${BASE}/candidats/boond/import-candidates`, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.error || `HTTP ${res.status}`);
      boondImportPoll.current.active = true;
      pollBoondImport();
    } catch (e) { showNotification('Erreur lancement import Boond : ' + e.message, 'error'); }
    finally { setBoondImportStarting(false); }
  };
  // Import candidats avec limite de test (Diagnostic Boond) ; confirmation si import complet
  const runBoondImport = async () => {
    const n = parseInt(boondImportLimit, 10);
    if (!n || n <= 0) {
      if (!await confirmErpia({ message: 'Aucune limite : tu vas importer TOUS les candidats Boond (~2102). Continuer ?' })) return;
      startBoondImport();
    } else {
      startBoondImport(n);
    }
  };
  // Reprend le suivi si un import tourne déjà à l'ouverture des sections IA ou Diagnostic Boond
  useEffect(() => {
    if ((section !== 'ia' && section !== 'boond') || boondImportPoll.current.active) return;
    fetch(`${BASE}/candidats/boond/import-candidates/status`, { headers })
      .then(r => r.json())
      .then(d => { setBoondImport(d); if (d.running) { boondImportPoll.current.active = true; pollBoondImport(); } })
      .catch(() => {});
    return () => { boondImportPoll.current.active = false; };
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Diagnostic Boond ──
  const testBoondPing = async () => {
    setBoondPinging(true); setBoondPing(null);
    try {
      const res = await fetch(`${BASE}/candidats/boond/ping`, { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) setBoondPing({ ok: true, sample: data.sample });
      else setBoondPing({ ok: false, error: data.error || `HTTP ${res.status}` });
    } catch (e) { setBoondPing({ ok: false, error: e.message }); }
    finally { setBoondPinging(false); }
  };
  const probeBoond = async (override) => {
    const ref = (override != null ? String(override) : boondRef).trim();
    if (!ref) return;
    if (override != null) setBoondRef(ref);
    setBoondProbing(true); setBoondProbe(null);
    try {
      const param = /^\d+$/.test(ref) ? `candidateId=${encodeURIComponent(ref)}` : `reference=${encodeURIComponent(ref)}`;
      const res = await fetch(`${BASE}/candidats/boond/probe?${param}`, { headers });
      const data = await res.json().catch(() => ({ error: `Réponse non-JSON (HTTP ${res.status})` }));
      setBoondProbe(data);
    } catch (e) { setBoondProbe({ error: e.message }); }
    finally { setBoondProbing(false); }
  };
  const copyProbe = () => { try { navigator.clipboard.writeText(JSON.stringify(boondProbe, null, 2)); showNotification('JSON copié', 'success'); } catch (e) {} };

  // Liste des candidats Boond (probe en lot)
  const listBoond = async () => {
    setBoondListing(true); setBoondList(null);
    try {
      const res = await fetch(`${BASE}/candidats/boond/probe-list`, { headers });
      const data = await res.json().catch(() => ({ error: `Réponse non-JSON (HTTP ${res.status})` }));
      setBoondList(data);
    } catch (e) { setBoondList({ error: e.message }); }
    finally { setBoondListing(false); }
  };
  const copyList = () => { try { navigator.clipboard.writeText(JSON.stringify(boondList, null, 2)); showNotification('JSON copié', 'success'); } catch (e) {} };

  // Route combinée PUT /config/users/:id — champ absent = non modifié. La ligne
  // ENREGISTRÉE revient dans data : on met le state à jour depuis elle (pas de
  // supposition). 409 = retirer le dernier admin actif → message back tel quel.
  const updateUserRole = async (userId, field, value) => {
    try {
      const r = await fetch(`${BASE}/config/users/${userId}`, { method: 'PUT', headers, body: JSON.stringify({ [field]: value }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const saved = j.data || { [field]: value };
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...saved } : u));
      showNotification('Rôle mis à jour', 'success');
    } catch (e) { showNotification(`Erreur : ${e.message}`, 'error'); }
  };

  // Édition prénom/nom : champs contrôlés + bouton « Sauvegarder » explicite
  // (l'ancien enregistrement onBlur ne donnait aucun retour et passait inaperçu).
  const [userEdits, setUserEdits] = useState({}); // { [id]: { prenom, nom } }
  const [userSaving, setUserSaving] = useState(null);
  const editVal = (u, f) => (userEdits[u.id]?.[f] ?? u[f] ?? '');
  const setEdit = (id, f, v) => setUserEdits(p => ({ ...p, [id]: { ...p[id], [f]: v } }));
  const userDirty = (u) => userEdits[u.id] && ((userEdits[u.id].prenom ?? u.prenom ?? '') !== (u.prenom ?? '') || (userEdits[u.id].nom ?? u.nom ?? '') !== (u.nom ?? ''));
  const saveUserInfo = async (u) => {
    const prenom = (userEdits[u.id]?.prenom ?? u.prenom ?? '').trim();
    const nom = (userEdits[u.id]?.nom ?? u.nom ?? '').trim();
    setUserSaving(u.id);
    try {
      // Route combinée recommandée : PUT /config/users/:id { prenom, nom }
      const r = await fetch(`${BASE}/config/users/${u.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ prenom, nom }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const saved = j.data || { prenom, nom }; // la ligne enregistrée fait foi
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, ...saved } : x));
      setUserEdits(p => { const n = { ...p }; delete n[u.id]; return n; });
      showNotification('Utilisateur mis à jour', 'success');
    } catch (e) { showNotification(`Erreur mise à jour : ${e.message}`, 'error'); }
    finally { setUserSaving(null); }
  };

  // ── Droits par utilisateur sur les apps Marketplace ──
  // user.modules = { crm: false, … } : décoché = accès retiré (clé absente =
  // accordé). L'admin voit toujours tout — la matrice est désactivée pour lui.
  const USER_MODULES = [
    ['sourcia', 'sourcia — diffusion sourceurs'],
    ['ressources', 'timesheet — Gestion → Ressources'],
    ['crm', 'crm — Gestion → CRM'],
    ['temptraining', 'temptraining — formations'],
    ['docsign', 'signd — signature électronique'],
    ['nda', 'nda — envoi de NDA'],
    ['tatlease', 'TATLease — gestion de flotte'],
  ];
  // Profils par rôle : pré-remplissent la matrice en un clic (ajustable après).
  // true = accès, false = retiré — toutes les clés sont posées explicitement.
  const MODULE_PRESETS = [
    { key: 'recruteur', label: 'Recruteur', emoji: '🎯', desc: 'Sourcing, signature & NDA — le quotidien du recrutement.', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', modules: { sourcia: true, docsign: true, nda: true, crm: false, ressources: false, temptraining: false } },
    { key: 'commercial', label: 'Commercial', emoji: '🤝', desc: 'CRM, clients, missions & NDA — la vente.', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', modules: { crm: true, docsign: true, nda: true, sourcia: false, ressources: false, temptraining: false } },
    { key: 'backoffice', label: 'Back-office RH', emoji: '🗂️', desc: 'Timesheets, formations & documents des consultants.', chip: 'bg-amber-50 text-amber-700 border-amber-200', modules: { ressources: true, temptraining: true, docsign: true, sourcia: false, crm: false, nda: false } },
    { key: 'manager', label: 'Manager', emoji: '👑', desc: 'Toute la Gestion, sans restriction.', chip: 'bg-gray-900 text-white border-gray-900', modules: { sourcia: true, ressources: true, crm: true, temptraining: true, docsign: true, nda: true } },
  ];
  // Profil détecté d'un utilisateur (chip de la matrice) : accès complet si
  // aucun refus, sinon le preset qui correspond, sinon « Personnalisé ».
  const detectProfile = (u) => {
    if (u.admin) return { label: 'admin — tout', chip: 'bg-gray-900 text-white border-gray-900' };
    const m = u.modules || {};
    if (!Object.values(m).some(v => v === false)) return { label: 'Accès complet', chip: 'bg-gray-50 text-gray-500 border-gray-200' };
    const hit = MODULE_PRESETS.find(p => USER_MODULES.every(([k]) => (m[k] !== false) === (p.modules[k] !== false)));
    if (hit) return { label: `${hit.emoji} ${hit.label}`, chip: hit.chip };
    return { label: '✳️ Personnalisé', chip: 'bg-violet-50 text-violet-700 border-violet-200' };
  };
  const [modulesFor, setModulesFor] = useState(null);   // user en édition
  const [modulesDraft, setModulesDraft] = useState({});
  const [modulesSaving, setModulesSaving] = useState(false);
  const presetActive = (preset) => USER_MODULES.every(([k]) => (modulesDraft[k] !== false) === (preset.modules[k] !== false));
  const openModules = (u) => { setModulesFor(u); setModulesDraft(u.modules && typeof u.modules === 'object' ? { ...u.modules } : {}); };
  // Écran « Rôles & droits » : chaque case sauvegarde immédiatement (optimiste,
  // rollback via fetchUsers en cas d'erreur). Presets = objet complet d'un coup.
  const putUserModules = async (u, next) => {
    setUsers(prev => prev.map(x => (x.id === u.id ? { ...x, modules: next } : x)));
    try {
      const r = await fetch(`${BASE}/config/users/${u.id}`, { method: 'PUT', headers, body: JSON.stringify({ modules: next }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const norm = j.data?.modules;
      if (norm) setUsers(prev => prev.map(x => (x.id === u.id ? { ...x, modules: norm } : x)));
    } catch (e) { showNotification('Erreur : ' + e.message, 'error'); fetchUsers(); }
  };
  const toggleUserModule = (u, key) => putUserModules(u, { ...(u.modules || {}), [key]: !((u.modules || {})[key] !== false) });
  const saveModules = async () => {
    setModulesSaving(true);
    try {
      const r = await fetch(`${BASE}/config/users/${modulesFor.id}`, { method: 'PUT', headers, body: JSON.stringify({ modules: modulesDraft }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // La réponse renvoie data.modules normalisé (uniquement les refus) —
      // on met la ligne à jour tout de suite, fetchUsers confirme derrière.
      const norm = j.data?.modules ?? modulesDraft;
      setUsers(prev => prev.map(x => (x.id === modulesFor.id ? { ...x, modules: norm } : x)));
      showNotification(`Modules de ${modulesFor.username} mis à jour`, 'success');
      setModulesFor(null);
      fetchUsers();
    } catch (e) { showNotification('Erreur : ' + e.message, 'error'); }
    finally { setModulesSaving(false); }
  };

  // ── Les rôles sont SERVIS, pas codés ici ──
  // Le serveur envoie `roles_admis` avec la liste (GET /config/users/list et
  // GET /users). C'est lui qui dit lesquels se cochent (`attribuable`) et
  // pourquoi les autres ne se cochent pas (`pourquoi`) : coder « Admin /
  // Recruteur » en dur ici est exactement ce qui avait rendu « Business
  // manager » inattribuable alors que le rôle existait des deux côtés.
  // Le repli reproduit l'écran d'avant si le serveur ne l'envoie pas encore.
  const [rolesAdmis, setRolesAdmis] = useState(null);
  const ROLES_REPLI = [
    { cle: 'admin', libelle: 'Admin', attribuable: true },
    { cle: 'recruteuse', libelle: 'Recruteur', attribuable: true },
  ];
  const rolesCochables = (rolesAdmis || ROLES_REPLI).filter(r => r.attribuable);
  // Les rôles qui se CONSTATENT (ressource) : affichés en pastille sur la
  // ligne, avec leur raison — les cacher ferait croire qu'ils n'existent pas.
  const rolesConstates = (rolesAdmis || []).filter(r => r.attribuable === false && r.cle !== 'interne');

  // ── Création / blocage / suppression d'utilisateurs (admin) ──
  const [showCreateUser, setShowCreateUser] = useState(false);
  const VIDE_CREATE = { username: '', email: '', prenom: '', nom: '', roles: {}, sendCreds: true };
  const [createForm, setCreateForm] = useState(VIDE_CREATE);
  const [creating, setCreating] = useState(false);
  const [tempPassword, setTempPassword] = useState(null); // { username, password } → modale à afficher une fois
  const [actingUser, setActingUser] = useState(null);     // id en cours (bloquer/supprimer)

  const createUser = async () => {
    const username = createForm.username.trim(), email = createForm.email.trim();
    if (!username || !email) { showNotification('Nom d\'utilisateur et email requis.', 'error'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${BASE}/config/users`, {
        method: 'POST', headers, body: JSON.stringify({
          username, email, prenom: createForm.prenom.trim(), nom: createForm.nom.trim(),
          // Les rôles cochés partent tels quels : le serveur connaît leurs clés,
          // le front n'en réécrit aucune.
          ...Object.fromEntries(rolesCochables.map(r => [r.cle, createForm.roles[r.cle] === true])),
          send_credentials: createForm.sendCreds && !!email,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { showNotification(j.error || j.message || 'Nom d\'utilisateur ou email déjà pris.', 'error'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setShowCreateUser(false);
      setCreateForm(VIDE_CREATE);
      if (j.credentials_sent === true) {
        // Accès partis par email : pas de mot de passe à transmettre à la main
        showNotification(`Identifiants envoyés par email à ${email}`, 'success');
      } else if (j.temp_password) {
        setTempPassword({ username, password: j.temp_password, sendError: j.send_error || null });
        showNotification('Utilisateur créé', 'success');
      } else {
        showNotification('Utilisateur créé', 'success');
      }
      fetchUsers();
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); }
    finally { setCreating(false); }
  };

  const toggleActive = async (u) => {
    const target = u.active === false; // bloqué → on réactive (true) ; actif → on bloque (false)
    setActingUser(u.id);
    try {
      const r = await fetch(`${BASE}/config/users/${u.id}/active`, { method: 'PUT', headers, body: JSON.stringify({ active: target }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 403 || r.status === 409) { showNotification(j.error || j.message || 'Action refusée (garde-fou).', 'error'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, active: target } : x));
      showNotification(target ? 'Utilisateur réactivé' : 'Utilisateur bloqué', 'success');
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); }
    finally { setActingUser(null); }
  };

  const deleteUser = async (u) => {
    if (!await confirmErpia({ message: `Supprimer ${u.username || u.email} ? (suppression logique)` })) return;
    setActingUser(u.id);
    try {
      const r = await fetch(`${BASE}/config/users/${u.id}`, { method: 'DELETE', headers });
      const j = await r.json().catch(() => ({}));
      if (r.status === 403 || r.status === 409) { showNotification(j.error || j.message || 'Suppression refusée (garde-fou).', 'error'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      showNotification('Utilisateur supprimé', 'success');
    } catch (e) { showNotification(`Échec : ${e.message}`, 'error'); }
    finally { setActingUser(null); }
  };

  const saveCommercial = async () => {
    if (!newCommercial.name || !newCommercial.email) return;
    setSavingCommercial(true);
    try {
      const updated = [...commerciaux, { ...newCommercial, id: Date.now() }];
      await fetch(`${BASE}/config/commerciaux`, { method: 'PUT', headers, body: JSON.stringify({ value: { list: updated } }) });
      setCommerciaux(updated);
      setNewCommercial({ name: '', title: 'Responsable Commercial', email: '', phone_office: '', phone_mobile: '' });
      showNotification('Contact ajouté', 'success');
    } catch (e) { showNotification('Erreur', 'error'); }
    setSavingCommercial(false);
  };

  const deleteCommercial = async (id) => {
    const updated = commerciaux.filter(c => c.id !== id);
    await fetch(`${BASE}/config/commerciaux`, { method: 'PUT', headers, body: JSON.stringify({ value: { list: updated } }) });
    setCommerciaux(updated);
    showNotification('Contact supprimé', 'success');
  };

  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Archivo',sans-serif", fontSize: '30px', fontWeight: 800, letterSpacing: '-0.025em' }}>
          <Settings className="w-6 h-6 text-gray-700" /> Configuration<span style={{ color: '#E2231A' }}>.</span>
        </h1>
        <p className="text-sm text-gray-500 mt-1">Paramètres globaux — visible admin uniquement</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-52 flex-shrink-0">
          <nav className="space-y-1">
            {[...SECTIONS.filter(s => !s.flag || features?.[s.flag]?.enabled_for_me !== false),
              // Entrée dessinée UNIQUEMENT si la sonde a reçu un 200 du serveur.
              ...(consoIA ? [{ id: 'conso_ia', label: 'Consommation IA', icon: Activity }] : [])].map(s => {
              const Icon = s.icon;
              return (
                <button key={s.id} onClick={() => setSection(s.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition ${section === s.id ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                  <Icon className="w-4 h-4" />{s.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">

          {/* ── NOTIFICATIONS ── */}
          {section === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Bell className="w-4 h-4 text-indigo-600" /> Notifications email
              </h2>
              {/* Règles configurables : c'est ici que se créent et s'activent
                  les notifications par déclencheur (dont les deux du Sourceur). */}
              <NotificationsRegles showNotification={showNotification} />
              <hr className="border-gray-100" />
              <p className="text-xs text-gray-400">
                Réglages hérités ci-dessous : clé d'envoi et listes d'adresses fixes, en place avant les règles.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                Les emails sont envoyés via <strong>Resend</strong>. Renseignez votre clé API ci-dessous.
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Clé API Resend</label>
                <input type="password" value={resendKey} onChange={e => setResendKey(e.target.value)}
                  placeholder="re_xxxxxxxxxxxxxxxx"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none font-mono" />
              </div>
              <hr className="border-gray-100" />
              {[
                { label: 'Besoin passé en P1 ou P2', value: notifP1P2, set: setNotifP1P2 },
                { label: '👤 Nouveau candidat ajouté',  value: notifNouveauCandidat, set: setNotifNouveauCandidat },
                { label: 'Rapport test / appel IA',  value: notifRapport, set: setNotifRapport },
              ].map((n, i) => (
                <div key={i} className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">{n.label}</label>
                  <input type="text" value={n.value} onChange={e => n.set(e.target.value)}
                    placeholder="email1@tat.ch, email2@tat.ch"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                </div>
              ))}
              <hr className="border-gray-100" />
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">🔔 Notifications in-app (cloche) — nombre maximum</label>
                <input type="number" min="1" max="100" value={notifInappMax} onChange={e => setNotifInappMax(e.target.value)}
                  className="w-32 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                <p className="text-xs text-gray-400">Seules les N dernières notifications sont affichées et conservées par utilisateur (défaut : 10).</p>
              </div>
              <button onClick={saveNotifications} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          )}

          {/* ── USERS ── */}
          {section === 'users' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-600" /> Utilisateurs & rôles
                </h2>
                <button onClick={() => setShowCreateUser(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-black transition">
                  <UserPlus className="w-4 h-4" /> Ajouter un utilisateur
                </button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Utilisateur</th>
                      {rolesCochables.map(r => (
                        <th key={r.cle} title={r.description || ''}
                          className="text-center px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{r.libelle}</th>
                      ))}
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Prénom / Nom</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Statut / Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.length === 0 && (
                      <tr><td colSpan={3 + rolesCochables.length} className="px-4 py-8 text-center text-sm text-gray-400">Aucun utilisateur à afficher.</td></tr>
                    )}
                    {users.map(u => {
                      const blocked = u.active === false;
                      const busy = actingUser === u.id;
                      return (
                      <tr key={u.id} className={`hover:bg-gray-50 ${blocked ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="w-7 h-7 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold uppercase">
                              {(u.username || u.prenom || u.email || '?')[0]}
                            </span>
                            <div>
                              <p className="font-medium text-gray-800">
                                {u.prenom || u.nom ? `${u.prenom || ''} ${u.nom || ''}`.trim() : u.username}
                              </p>
                              {u.username && <p className="text-xs text-gray-500">@{u.username}</p>}
                              <p className="text-xs text-gray-400">{u.email}</p>
                              {/* Un rôle qui ne se coche pas se CONSTATE : on le
                                  montre avec sa raison plutôt que de le cacher. */}
                              {rolesConstates.filter(r => u[r.cle] === true).map(r => (
                                <span key={r.cle} title={r.pourquoi || r.description || ''}
                                  className="inline-block mt-1 text-[10.5px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 rounded px-1.5 py-0.5">
                                  {r.libelle}
                                </span>
                              ))}
                            </div>
                          </div>
                        </td>
                        {rolesCochables.map(r => (
                          <td key={r.cle} className="px-4 py-3 text-center">
                            <input type="checkbox" checked={u[r.cle] === true} disabled={blocked}
                              aria-label={`${r.libelle} — ${u.nom_affiche || u.username}`}
                              onChange={e => updateUserRole(u.id, r.cle, e.target.checked)}
                              className="w-4 h-4 accent-indigo-600 cursor-pointer disabled:opacity-40" />
                          </td>
                        ))}
                        <td className="px-4 py-3">
                          <div className="flex gap-1 items-center">
                            <input placeholder="Prénom" value={editVal(u, 'prenom')}
                              onChange={e => setEdit(u.id, 'prenom', e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' && userDirty(u)) saveUserInfo(u); }}
                              className="text-xs border border-gray-200 rounded px-2 py-1 w-20 outline-none focus:border-indigo-400" />
                            <input placeholder="Nom" value={editVal(u, 'nom')}
                              onChange={e => setEdit(u.id, 'nom', e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' && userDirty(u)) saveUserInfo(u); }}
                              className="text-xs border border-gray-200 rounded px-2 py-1 w-24 outline-none focus:border-indigo-400" />
                            <button onClick={() => saveUserInfo(u)} disabled={!userDirty(u) || userSaving === u.id}
                              className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-default">
                              <Save className="w-3 h-3" /> {userSaving === u.id ? '…' : 'Sauvegarder'}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {blocked && <span className="text-[10.5px] font-semibold bg-red-50 text-red-600 rounded px-2 py-0.5">Bloqué</span>}
                            <button onClick={() => openModules(u)} disabled={busy || !!u.admin}
                              title={u.admin ? 'Un admin voit toujours tous les modules' : 'Choisir les modules visibles pour cet utilisateur'}
                              className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                              <LayoutGrid className="w-3.5 h-3.5" /> Modules
                            </button>
                            <button onClick={() => toggleActive(u)} disabled={busy} title={blocked ? 'Réactiver l\'accès' : 'Bloquer l\'accès'}
                              className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                              {blocked ? <><Unlock className="w-3.5 h-3.5" /> Débloquer</> : <><Lock className="w-3.5 h-3.5" /> Bloquer l'accès</>}
                            </button>
                            <button onClick={() => deleteUser(u)} disabled={busy} title="Supprimer"
                              className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50 disabled:opacity-40">
                              <Trash2 className="w-3.5 h-3.5" /> Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modale — Créer un utilisateur */}
          {showCreateUser && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => !creating && setShowCreateUser(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2"><UserPlus className="w-4 h-4 text-gray-700" /> Nouvel utilisateur</h3>
                  <button onClick={() => setShowCreateUser(false)} disabled={creating} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Nom d'utilisateur *</label>
                      <input value={createForm.username} autoFocus onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="jdupont" /></div>
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Email *</label>
                      <input value={createForm.email} type="email" onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="j.dupont@…" /></div>
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Prénom</label>
                      <input value={createForm.prenom} onChange={e => setCreateForm(f => ({ ...f, prenom: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                    <div><label className="block text-xs font-medium text-gray-500 mb-1">Nom</label>
                      <input value={createForm.nom} onChange={e => setCreateForm(f => ({ ...f, nom: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                  </div>
                  <div className="flex items-center gap-5 pt-1 flex-wrap">
                    {rolesCochables.map(r => (
                      <label key={r.cle} title={r.description || ''} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={createForm.roles[r.cle] === true}
                          onChange={e => setCreateForm(f => ({ ...f, roles: { ...f.roles, [r.cle]: e.target.checked } }))}
                          className="w-4 h-4 accent-indigo-600" /> {r.libelle}
                      </label>
                    ))}
                  </div>
                  {rolesConstates.length > 0 && (
                    <p className="text-[11px] text-gray-400">
                      {rolesConstates.map(r => `${r.libelle} : ${r.pourquoi}`).join(' ')}
                    </p>
                  )}
                  <label className={`flex items-center gap-2 text-sm cursor-pointer ${createForm.email.trim() ? 'text-gray-700' : 'text-gray-400 cursor-not-allowed'}`}>
                    <input type="checkbox" checked={createForm.sendCreds && !!createForm.email.trim()} disabled={!createForm.email.trim()}
                      onChange={e => setCreateForm(f => ({ ...f, sendCreds: e.target.checked }))}
                      className="w-4 h-4 accent-indigo-600 disabled:opacity-40" />
                    Envoyer les accès par email{!createForm.email.trim() && ' (saisissez un email)'}
                  </label>
                  <p className="text-[11px] text-gray-400">Sans envoi par email, un mot de passe temporaire sera affiché une seule fois — à transmettre vous-même.</p>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <button onClick={() => setShowCreateUser(false)} disabled={creating} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">Annuler</button>
                  <button onClick={createUser} disabled={creating || !createForm.username.trim() || !createForm.email.trim()}
                    className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-black disabled:opacity-40 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> {creating ? 'Création…' : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modale — Mot de passe temporaire (affiché une fois) */}
          {tempPassword && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="px-6 py-5">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Lock className="w-4 h-4 text-gray-700" /> Mot de passe temporaire</h3>
                  <p className="text-sm text-gray-500 mt-2">Compte <b>@{tempPassword.username}</b> créé. Copiez ce mot de passe et transmettez-le — <b>il ne sera plus affiché.</b></p>
                  <div className="mt-4 flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50">
                    <code className="flex-1 font-mono text-sm text-gray-800 break-all">{tempPassword.password}</code>
                    <button onClick={() => { try { navigator.clipboard.writeText(tempPassword.password); showNotification('Mot de passe copié', 'success'); } catch {} }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg hover:bg-black flex-none"><Copy className="w-3.5 h-3.5" /> Copier</button>
                  </div>
                  {tempPassword.sendError && (
                    <p className="mt-3 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">L'envoi par email a échoué : {tempPassword.sendError} — transmettez le mot de passe à la main.</p>
                  )}
                </div>
                <div className="flex items-center justify-end px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <button onClick={() => setTempPassword(null)} className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-black">J'ai copié le mot de passe</button>
                </div>
              </div>
            </div>
          )}

          {/* ── Droits modules d'un utilisateur ── */}
          {modulesFor && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => setModulesFor(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-5">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-gray-700" /> Modules de @{modulesFor.username}</h3>
                  <p className="text-sm text-gray-500 mt-2">Décochez un module pour le retirer à cet utilisateur. Seuls les modules <b>activés au Marketplace</b> sont concernés — un module désactivé au niveau de l'organisation reste invisible pour tout le monde.</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {MODULE_PRESETS.map(p => {
                      const active = presetActive(p);
                      return (
                        <button key={p.key} onClick={() => setModulesDraft({ ...p.modules })} title={p.desc}
                          className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition ${active ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                          {p.emoji} {p.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {USER_MODULES.map(([key, label]) => {
                      const allowed = modulesDraft[key] !== false;
                      return (
                        <label key={key} className="flex items-center gap-3 px-3 py-2 rounded-xl border border-gray-100 hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={allowed}
                            onChange={e => setModulesDraft(p => ({ ...p, [key]: e.target.checked }))}
                            className="w-4 h-4 accent-indigo-600 cursor-pointer" />
                          <span className={`text-sm ${allowed ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <button onClick={() => setModulesFor(null)} className="px-4 py-2 text-sm font-medium text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-100">Annuler</button>
                  <button onClick={saveModules} disabled={modulesSaving}
                    className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-black disabled:opacity-50">
                    <Save className="w-3.5 h-3.5" /> {modulesSaving ? 'Sauvegarde…' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── RÔLES & DROITS — matrice utilisateurs × modules ── */}
          {section === 'droits' && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Shield className="w-4 h-4 text-indigo-600" /> Rôles & droits
              </h2>
              <p className="text-sm text-gray-500">Qui voit quels modules. Une case décochée retire le module à la personne (même s'il est activé au Marketplace). Chaque clic sauvegarde immédiatement. Les <b>admins</b> voient toujours tout.</p>

              {/* Cartes de profil — la définition visuelle de chaque rôle */}
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
                {MODULE_PRESETS.map(p => (
                  <div key={p.key} className="rounded-xl border border-gray-200 p-3.5 bg-white">
                    <div className="flex items-center gap-2">
                      <span className="text-lg leading-none">{p.emoji}</span>
                      <span className="text-sm font-bold text-gray-800">{p.label}</span>
                    </div>
                    <p className="text-[11.5px] text-gray-500 mt-1.5 leading-snug">{p.desc}</p>
                    <div className="flex flex-wrap gap-1 mt-2.5">
                      {USER_MODULES.filter(([k]) => p.modules[k] !== false).map(([k]) => (
                        <span key={k} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{k === 'ressources' ? 'timesheet' : k}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {(() => {
                // Internes uniquement : les comptes « ressource » n'ont que Mon
                // espace — la matrice de modules ne les concerne pas.
                const internes = users.filter(u => !(u.role === 'ressource' || u.ressource === true));
                return (
                  <>
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Utilisateur</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500">Profil</th>
                            {USER_MODULES.map(([key]) => (
                              <th key={key} className="text-center px-2 py-3 text-[11px] font-semibold text-gray-500 whitespace-nowrap">{key === 'ressources' ? 'timesheet' : key}</th>
                            ))}
                            <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500">Appliquer un profil</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {internes.length === 0 && (
                            <tr><td colSpan={USER_MODULES.length + 3} className="px-4 py-8 text-center text-sm text-gray-400">Aucun utilisateur interne à afficher.</td></tr>
                          )}
                          {internes.map(u => {
                            const isAdmin = !!u.admin;
                            const prof = detectProfile(u);
                            return (
                              <tr key={u.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <span className="w-7 h-7 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold uppercase">{(u.username || u.email || '?')[0]}</span>
                                    <div>
                                      <p className="font-medium text-gray-800">@{u.username}</p>
                                      <p className="text-[11px] text-gray-400">{isAdmin ? 'admin' : u.recruteuse ? 'recruteur' : 'utilisateur'}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-3">
                                  <span className={`inline-flex items-center text-[10.5px] font-semibold px-2 py-1 rounded-md border whitespace-nowrap ${prof.chip}`}>{prof.label}</span>
                                </td>
                                {USER_MODULES.map(([key]) => {
                                  const allowed = isAdmin || (u.modules || {})[key] !== false;
                                  return (
                                    <td key={key} className="text-center px-2 py-3">
                                      <input type="checkbox" checked={allowed} disabled={isAdmin}
                                        onChange={() => toggleUserModule(u, key)}
                                        title={isAdmin ? 'Un admin voit toujours tous les modules' : allowed ? `Retirer ${key} à @${u.username}` : `Rendre ${key} à @${u.username}`}
                                        className="w-4 h-4 accent-indigo-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" />
                                    </td>
                                  );
                                })}
                                <td className="text-center px-3 py-3">
                                  {isAdmin ? <span className="text-[11px] text-gray-300">—</span> : (
                                    <ErpiaSelect value="" onChange={e => { const p = MODULE_PRESETS.find(x => x.key === e.target.value); if (p) putUserModules(u, { ...p.modules }); }}
                                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-indigo-400 cursor-pointer bg-white text-gray-600">
                                      <option value="" disabled>⚡ Choisir…</option>
                                      {MODULE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.emoji} {p.label}</option>)}
                                    </ErpiaSelect>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-gray-400">Les comptes <b>ressource</b> (consultants) n'apparaissent pas ici — leur accès se limite à « Mon espace ». Les modules ne sont visibles que s'ils sont aussi <b>activés au Marketplace</b> (niveau organisation). Le refus est appliqué côté serveur (403 « retiré pour votre compte ») — effet au prochain chargement de la personne.</p>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── TEMPLATES (CV entreprise, contrats DOCX & emails) ── */}
          {section === 'templates' && (
            <div className="space-y-6">
              <CvTemplateCard showNotification={showNotification} />
              <TemplatesSection showNotification={showNotification} />
            </div>
          )}

          {/* Sourceurs (sourcia) : déplacé dans Marketplace → application sourcia */}

          {/* ── IA ── */}
          {section === 'ia' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Settings className="w-4 h-4 text-indigo-600" /> Intelligence Artificielle
              </h2>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-700">
                Par défaut l'app utilise <strong>Claude (Anthropic)</strong>. Activez Leman pour utiliser votre propre agent IA.
              </div>
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                <div>
                  <p className="font-medium text-gray-800">Activer Leman</p>
                  <p className="text-xs text-gray-400 mt-0.5">Remplace Claude par l'agent Leman de TAT</p>
                </div>
                <button onClick={() => setLemanEnabled(v => !v)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${lemanEnabled ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${lemanEnabled ? 'left-7' : 'left-1'}`} />
                </button>
              </div>
              {lemanEnabled && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL de l'API Leman</label>
                    <input type="text" value={lemanUrl} onChange={e => setLemanUrl(e.target.value)}
                      placeholder="https://leman.talentaccess.ch/api/chat"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none font-mono" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Clé API Leman</label>
                    <input type="password" value={lemanKey} onChange={e => setLemanKey(e.target.value)}
                      placeholder="leman_xxxxxxxxxxxxxxxx"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none font-mono" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modèle (optionnel)</label>
                    <input type="text" value={lemanModel} onChange={e => setLemanModel(e.target.value)}
                      placeholder="Ex: leman-pro..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                  </div>
                </div>
              )}
              <div className={`flex items-center gap-3 p-4 rounded-xl border ${lemanEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <span className={`w-3 h-3 rounded-full ${lemanEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                <div>
                  <p className="text-sm font-medium text-gray-800">{lemanEnabled ? 'Leman activé' : 'Claude (Anthropic) actif'}</p>
                  <p className="text-xs text-gray-400">{lemanEnabled ? lemanUrl || 'URL non configurée' : 'claude-sonnet-4-20250514'}</p>
                </div>
              </div>
              <hr className="border-gray-100" />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">🎙️ Enregistrements d'entretiens</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durée de rétention</label>
                  <ErpiaSelect value={audioRetention} onChange={e => setAudioRetention(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none">
                    <option value="7">7 jours</option>
                    <option value="30">30 jours</option>
                    <option value="60">60 jours</option>
                    <option value="90">90 jours</option>
                    <option value="365">1 an</option>
                    <option value="0">Illimité</option>
                  </ErpiaSelect>
                </div>
                <button onClick={deleteAllAudio} disabled={deletingAudio}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm hover:bg-red-100 transition disabled:opacity-50">
                  <Trash2 className="w-4 h-4" /> {deletingAudio ? 'Suppression...' : 'Supprimer tous les enregistrements'}
                </button>
              </div>

              <hr className="border-gray-100" />
              {/* ── Debug : registre d'outils de l'assistant (GET /agent/tools) ── */}
              <AgentToolsDebug showNotification={showNotification} />

              <hr className="border-gray-100" />
              {/* ── Rattrapage des profils CV ── */}
              {(() => {
                const running = !!backfill?.running;
                const total = backfill?.total || 0;
                const done = backfill?.done || 0;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">Profils CV (compétences & résumé)</h3>
                    <p className="text-xs text-gray-500">
                      Les nouveaux candidats sont extraits automatiquement à l'upload du CV. Ce bouton rattrape l'<strong>historique</strong> : il (ré)extrait les profils des candidats déjà en base dont le profil est vide.
                    </p>
                    <button onClick={startBackfill} disabled={running || backfillStarting}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition disabled:opacity-50">
                      <i className={`bi ${running || backfillStarting ? 'bi-arrow-repeat animate-spin' : 'bi-file-earmark-text'}`} />
                      {running ? 'Extraction en cours…' : backfillStarting ? 'Lancement…' : 'Réextraire les profils CV'}
                    </button>

                    {running && (
                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                          <span>Analyse des CV…</span>
                          <span className="font-mono">{done}/{total}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )}

                    {backfill && !running && (backfill.done != null) && (
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2.5 py-1 rounded-lg bg-green-50 text-green-700 font-medium">{backfill.ok ?? 0} profil(s) rempli(s)</span>
                        {(backfill.failed ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-red-50 text-red-600 font-medium">{backfill.failed} échec(s)</span>}
                        {(backfill.skipped ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-500 font-medium">{backfill.skipped} sans CV</span>}
                      </div>
                    )}

                    {!running && backfill?.errors?.length > 0 && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-100">Échecs ({backfill.errors.length})</div>
                        <div className="max-h-44 overflow-auto divide-y divide-gray-50">
                          {backfill.errors.map((e, i) => (
                            <div key={i} className="px-3 py-2 text-xs flex items-center gap-2">
                              <span className="font-medium text-gray-700 truncate" style={{ maxWidth: '180px' }}>{e.nom || `#${e.id}`}</span>
                              <span className="text-red-500 truncate flex-1">{e.error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <hr className="border-gray-100" />
              {/* ── Extraction du texte des CV (sans IA) ── */}
              {(() => {
                const running = !!cvText?.running;
                const total = cvText?.total || 0;
                const done = cvText?.done || 0;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">Texte des CV (pour le matching)</h3>
                    <p className="text-xs text-gray-500">
                      Extrait le <strong>texte brut</strong> des CV déjà en base (téléchargement + lecture, <strong>sans IA</strong>) — indispensable pour que le matching AO note correctement les candidats. Ne retraite que les CV dont le texte est vide.
                    </p>
                    <button onClick={startCvText} disabled={running || cvTextStarting}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition disabled:opacity-50">
                      <i className={`bi ${running || cvTextStarting ? 'bi-arrow-repeat animate-spin' : 'bi-file-earmark-text'}`} />
                      {running ? 'Extraction en cours…' : cvTextStarting ? 'Lancement…' : 'Extraire le texte des CV'}
                    </button>

                    {running && (
                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                          <span>Lecture des CV…</span>
                          <span className="font-mono">{done}/{total}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )}

                    {cvText && !running && (cvText.done != null) && (
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2.5 py-1 rounded-lg bg-green-50 text-green-700 font-medium">{cvText.ok ?? 0} texte(s) extrait(s)</span>
                        {(cvText.failed ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-red-50 text-red-600 font-medium">{cvText.failed} échec(s)</span>}
                        {(cvText.skipped ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-500 font-medium">{cvText.skipped} ignoré(s)</span>}
                      </div>
                    )}

                    {!running && cvText?.errors?.length > 0 && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-100">Échecs ({cvText.errors.length})</div>
                        <div className="max-h-44 overflow-auto divide-y divide-gray-50">
                          {cvText.errors.map((e, i) => (
                            <div key={i} className="px-3 py-2 text-xs flex items-center gap-2">
                              <span className="font-medium text-gray-700 truncate" style={{ maxWidth: '180px' }}>{e.nom || `#${e.id}`}</span>
                              <span className="text-red-500 truncate flex-1">{e.error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <hr className="border-gray-100" />
              {/* ── Import des documents Boond ── */}
              {(() => {
                const running = !!boondImport?.running;
                const total = boondImport?.total || 0;
                const done = boondImport?.done || 0;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">👥 Candidats Boond</h3>
                    <p className="text-xs text-gray-500">
                      Synchronise les candidats depuis Boond : crée / met à jour les fiches dans l'ERP <strong>et</strong> importe leurs CV & pièces jointes. L'import est <strong>idempotent</strong> : relancer ne crée pas de doublons.
                    </p>
                    <button onClick={() => startBoondImport()} disabled={running || boondImportStarting}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition disabled:opacity-50">
                      <i className={`bi ${running || boondImportStarting ? 'bi-arrow-repeat animate-spin' : 'bi-cloud-download'}`} />
                      {running ? 'Import en cours…' : boondImportStarting ? 'Lancement…' : 'Importer les candidats depuis Boond'}
                    </button>

                    {running && (
                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                          <span>Synchronisation depuis Boond…</span>
                          <span className="font-mono">{done}/{total}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )}

                    {boondImport && !running && (boondImport.done != null || boondImport.created != null || boondImport.resumes_ok != null) && (
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2.5 py-1 rounded-lg bg-green-50 text-green-700 font-medium">{boondImport.created ?? 0} candidat(s) créé(s)</span>
                        {(boondImport.updated ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-medium">{boondImport.updated} mis à jour</span>}
                        <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 font-medium">{boondImport.resumes_ok ?? 0} CV · {boondImport.files_ok ?? 0} pièce(s)</span>
                        {(boondImport.failed ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-red-50 text-red-600 font-medium">{boondImport.failed} échec(s)</span>}
                        {(boondImport.skipped ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-500 font-medium">{boondImport.skipped} ignoré(s)</span>}
                      </div>
                    )}

                    {!running && boondImport?.errors?.length > 0 && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-100">Échecs ({boondImport.errors.length})</div>
                        <div className="max-h-44 overflow-auto divide-y divide-gray-50">
                          {boondImport.errors.map((e, i) => (
                            <div key={i} className="px-3 py-2 text-xs flex items-center gap-2">
                              <span className="font-medium text-gray-700 truncate" style={{ maxWidth: '180px' }}>{e.boond_ref ? `Boond ${e.boond_ref}` : `#${e.candidate_id}`}</span>
                              <span className="text-red-500 truncate flex-1">{e.error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <button onClick={saveIA} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          )}

          {/* ── COMMERCIAUX ── */}
          {section === 'commerciaux' && (
            <div className="space-y-5">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-600" /> Contacts commerciaux CV
              </h2>
              <p className="text-sm text-gray-500">Ces contacts apparaissent dans le CV Processor comme responsable à associer au CV.</p>
              <div className="space-y-2">
                {commerciaux.length === 0 && (
                  <p className="text-sm text-gray-400 italic">Aucun contact configuré — les contacts par défaut sont utilisés.</p>
                )}
                {commerciaux.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                    <div>
                      <p className="font-medium text-sm text-gray-800">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.title}</p>
                      <p className="text-xs text-gray-400">{c.email} · {c.phone_office}{c.phone_mobile ? ` | ${c.phone_mobile}` : ''}</p>
                    </div>
                    <button onClick={() => deleteCommercial(c.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <hr className="border-gray-100" />
              <h3 className="text-sm font-semibold text-gray-700">Ajouter un contact</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Nom complet *</label>
                  <input value={newCommercial.name} onChange={e => setNewCommercial(p => ({...p, name: e.target.value}))}
                    placeholder="Ex: Domingos MUIKA"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Titre</label>
                  <input value={newCommercial.title} onChange={e => setNewCommercial(p => ({...p, title: e.target.value}))}
                    placeholder="Responsable Commercial"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Email *</label>
                  <input type="email" value={newCommercial.email} onChange={e => setNewCommercial(p => ({...p, email: e.target.value}))}
                    placeholder="prenom.nom@talentaccess.ch"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Tél. bureau</label>
                  <input value={newCommercial.phone_office} onChange={e => setNewCommercial(p => ({...p, phone_office: e.target.value}))}
                    placeholder="+41 22 760 25 87"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Tél. mobile</label>
                  <input value={newCommercial.phone_mobile} onChange={e => setNewCommercial(p => ({...p, phone_mobile: e.target.value}))}
                    placeholder="+41 78 642 90 35"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                </div>
              </div>
              <button onClick={saveCommercial} disabled={savingCommercial || !newCommercial.name || !newCommercial.email}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                <Save className="w-4 h-4" /> {savingCommercial ? 'Ajout...' : 'Ajouter le contact'}
              </button>
            </div>
          )}

          {section === 'flow' && <FlowTemplate showNotification={showNotification} />}

          {/* ── ALGORITHME DE MATCHING (poids par défaut) ── */}
          {section === 'matching' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-indigo-600" /> Algorithme de matching
              </h2>
              <MatchingConfigSection showNotification={showNotification} />
              <DiagnosticScoringSection showNotification={showNotification} />
            </div>
          )}

          {/* ── LOGO DES DOCUMENTS ── */}
          {section === 'marque' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-indigo-600" /> Logo
              </h2>
              <p className="text-sm text-gray-500">Votre logo, utilisé partout : en tête des documents générés (fiches de poste, CV format entreprise…) et dans l'application (barre latérale). Propre à votre organisation.</p>

              <div className="flex items-center gap-5 flex-wrap">
                <div className="flex items-center justify-center bg-gray-50 border border-gray-200 rounded-xl" style={{ width: '220px', height: '96px', padding: '10px' }}>
                  {/* Pas de logo configuré : la marque neutre embarquée, pas
                      celle d'un client — ce produit est en marque blanche. */}
                  <img key={logoVer} src={logoAbsent ? logoDefaut : (logoSrc || undefined)} alt="Logo des documents"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', opacity: logoAbsent ? 0.45 : 1 }}
                    onError={() => setLogoAbsent(true)} />
                </div>
                <div className="flex flex-col gap-2">
                  <input ref={logoFileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; uploadLogo(f); }} />
                  <button onClick={() => logoFileRef.current?.click()} disabled={logoUploading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                    <ImageIcon className="w-4 h-4" /> {logoUploading ? 'Envoi…' : 'Changer le logo'}
                  </button>
                  <button onClick={resetLogo} disabled={logoUploading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                    <Trash2 className="w-4 h-4" /> Réinitialiser
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                PNG ou JPEG, max 2 Mo. Le SVG n'est pas supporté (le moteur PDF ne le lit pas). « Réinitialiser » revient au logo par défaut.
              </div>
            </div>
          )}

          {/* ── CONTRATS (valeurs par défaut) ── */}
          {section === 'contrats' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" /> Contrats
              </h2>
              <p className="text-sm text-gray-500">
                Valeurs utilisées quand un contrat est généré sans que l'information soit précisée sur le lot
                ou sur la fiche du candidat. Elles valent pour toute l'organisation ; une valeur saisie sur
                un lot l'emporte toujours.
              </p>

              {ctrDef == null ? (
                <div className="text-sm text-gray-400">Chargement…</div>
              ) : (
                <div className="space-y-4 max-w-md">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Date de début par défaut</label>
                    <input type="date" value={String(ctrDef.date_debut || '').slice(0, 10)}
                      onChange={e => setCtrDef(d => ({ ...d, date_debut: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Type d'emploi par défaut</label>
                    <input type="text" value={ctrDef.type_contrat || ''} placeholder="Employé spécialisé"
                      onChange={e => setCtrDef(d => ({ ...d, type_contrat: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-sm font-medium">Salaire du contrat</span>
                    <p className="text-sm" style={{ color: 'var(--text-3)' }}>
                      Pas de valeur par défaut : le salaire de chaque contrat suit le TJM de son lot
                      (marge de 40 % au plus, arrondi à la dizaine de milliers).
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Adresse par défaut</label>
                    <input type="text" value={ctrDef.adresse || ''} placeholder="Rue, numéro, ville"
                      onChange={e => setCtrDef(d => ({ ...d, adresse: e.target.value }))}
                      className="erp-field w-full" />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Civilité par défaut</label>
                    <select value={ctrDef.civilite || ''}
                      onChange={e => setCtrDef(d => ({ ...d, civilite: e.target.value }))}
                      className="erp-field w-full">
                      <option value="">Aucune</option>
                      <option value="Monsieur">Monsieur</option>
                      <option value="Madame">Madame</option>
                    </select>
                  </div>
                  <button onClick={enregistrerCtrDef} disabled={ctrDefBusy}
                    className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                    <Save className="w-4 h-4" /> {ctrDefBusy ? 'Enregistrement…' : 'Enregistrer'}
                  </button>

                  {ctrDefAbsent && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-600 leading-relaxed">
                      L'enregistrement depuis cet écran arrive côté serveur — rien n'a été modifié.
                      En attendant, demandez à l'assistant :<br />
                      <span className="font-mono text-[11px] bg-white border border-gray-200 rounded px-1.5 py-0.5 inline-block mt-1.5">
                        mets le type d'emploi par défaut à Employé spécialisé et la date de début au 01.08.2026
                      </span>
                    </div>
                  )}
                  {ctrDefPerdus.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                      Non conservé par le serveur : {ctrDefPerdus.join(', ')}. Le nom exact des champs est
                      peut-être différent — vérifiez avec l'assistant, qui utilise le même réglage.
                    </div>
                  )}
                </div>
              )}

              <hr className="border-gray-100" />

              {/* ── Récapitulatif client (classeur Excel) ── */}
              <div className="text-sm font-semibold text-gray-800">Récapitulatif client</div>
              <p className="text-sm text-gray-500">
                Le bloc société écrit dans le classeur Excel du client, et les règles de remplissage.
                Tant que le tarif journalier n'est pas « au prix max », les cellules de tarif restent à saisir à la main.
              </p>
              {recap == null ? (
                <div className="text-sm text-gray-400">Chargement…</div>
              ) : (
                <div className="space-y-4 max-w-md">
                  <div className="grid grid-cols-2 gap-3">
                    {CHAMPS_SOCIETE.map(([k, label]) => (
                      <div key={k} className={/adresse|contact/.test(k) ? 'col-span-2 space-y-1' : 'space-y-1'}>
                        <label className="block text-xs font-medium text-gray-600">{label}</label>
                        <input type="text" value={recap.societe?.[k] || ''}
                          onChange={e => setRecap(d => ({ ...d, societe: { ...(d.societe || {}), [k]: e.target.value } }))}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Taux par défaut (%)</label>
                      <input type="number" min="1" max="100" value={recap.taux_defaut ?? ''}
                        onChange={e => setRecap(d => ({ ...d, taux_defaut: e.target.value }))}
                        placeholder="100"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Tarif journalier</label>
                      <div className="flex gap-2">
                        {[['prix_max', 'Au prix max'], ['aucun', 'À saisir']].map(([v, label]) => (
                          <button key={v} onClick={() => setRecap(d => ({ ...d, tarif_defaut: v }))}
                            className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border transition ${(recap.tarif_defaut || 'aucun') === v ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {recap.permis_defaut !== undefined && (
                      <div className="space-y-1">
                        <label className="block text-xs font-medium">Permis par défaut</label>
                        <select value={recap.permis_defaut || ''} onChange={e => setRecap(d => ({ ...d, permis_defaut: e.target.value }))}
                          className="erp-field w-full" title="Posé quand la fiche n'a pas de permis et que sa nationalité est connue — jamais pour une nationalité suisse">
                          <option value="">À saisir</option>
                          {['B', 'G', 'C', 'Ci', 'L', 'F', 'N'].map(v => <option key={v} value={v}>Permis {v} (sauf Suisses)</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  <button onClick={enregistrerRecap} disabled={recapBusy}
                    className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                    <Save className="w-4 h-4" /> {recapBusy ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  {recapErreur && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 leading-relaxed">{recapErreur}</div>
                  )}
                  {recapAbsent && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-600 leading-relaxed">
                      L'enregistrement depuis cet écran arrive côté serveur — rien n'a été modifié.
                      En attendant, demandez à l'assistant :<br />
                      <span className="font-mono text-[11px] bg-white border border-gray-200 rounded px-1.5 py-0.5 inline-block mt-1.5">
                        mets le tarif journalier par défaut au prix max
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── SIGNATURE ÉLECTRONIQUE (tampon de contresignature) ── */}
          {section === 'candidatures' && <BoiteCandidatures showNotification={showNotification} />}
          {section === 'docsign' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FileSignature className="w-4 h-4 text-indigo-600" /> Signature électronique
              </h2>
              <p className="text-sm text-gray-500">
                Le tampon de votre entreprise, apposé sur les contrats que vous contresignez depuis un dossier AO.
                Tant qu’aucun tampon n’est enregistré ici, la case « apposer le tampon » reste grisée au moment de générer les contrats.
              </p>

              {docsignAbsent && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-600">
                  Cette fonctionnalité arrive côté serveur — rien n’est enregistré pour l’instant.
                </div>
              )}

              {/* ── Qui dessine le sceau du candidat ── */}
              {rendu && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold text-gray-800">Qui dessine le sceau de signature</div>
                  <p className="text-xs text-gray-500">
                    Le sceau apposé à côté de la signature du candidat sur le contrat.
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {[
                      {
                        cle: 'erpia',
                        titre: 'L’application',
                        texte: 'Le sceau est dessiné par erpia. C’est le rendu de référence, celui que vous voyez à l’aperçu.',
                        badge: 'rendu normal',
                        badgeClass: 'bg-emerald-50 text-emerald-700',
                      },
                      {
                        cle: 'docsign',
                        titre: 'Le prestataire de signature',
                        texte: 'Le sceau est dessiné par le prestataire. Son rendu n’est pas encore validé — à réserver aux essais.',
                        badge: 'expérimental',
                        badgeClass: 'bg-amber-50 text-amber-700',
                      },
                    ].map(opt => {
                      const actif = rendu.valeur === opt.cle;
                      return (
                        <button key={opt.cle} type="button" disabled={renduBusy}
                          onClick={() => definirRendu(opt.cle)}
                          className={`text-left rounded-xl border-2 p-3 transition ${actif ? 'border-indigo-500 bg-indigo-50/40' : 'border-gray-200 hover:border-gray-300'} ${renduBusy ? 'opacity-60 cursor-wait' : ''}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-800">{opt.titre}</span>
                            <span className={`text-[10.5px] rounded px-1.5 py-0.5 ${opt.badgeClass}`}>{opt.badge}</span>
                            {rendu.defaut === opt.cle && <span className="text-[10.5px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">par défaut</span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{opt.texte}</p>
                        </button>
                      );
                    })}
                  </div>
                  {rendu.valeur !== 'erpia' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                      Vous êtes en mode d’essai : le sceau n’est plus dessiné par l’application. Vérifiez le contrat produit
                      avant de l’envoyer à un candidat.
                    </div>
                  )}
                </div>
              )}

              {/* Apparence du sceau : figée, et l'écran dit pourquoi. Un
                  réglage absent sans explication passe pour un oubli. */}
              {sceauInfo?.apparence_reglable === false && (
                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-800">Apparence du sceau</span>
                    <span className="text-[10.5px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">non modifiable</span>
                  </div>
                  {sceauInfo.raison && <p className="text-xs text-gray-500 leading-relaxed">{sceauInfo.raison}</p>}
                  {Array.isArray(sceauInfo.reglages_disponibles) && sceauInfo.reglages_disponibles.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1.5">Ce qui reste réglable</div>
                      <ul className="space-y-1">
                        {sceauInfo.reglages_disponibles.map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                            <span className="text-gray-300 mt-0.5">•</span>
                            <span><span className="text-gray-700 font-medium">{r.quoi}</span>{r.ou ? ` — ${r.ou}` : ''}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <hr className="border-gray-100" />

              <div className="text-sm font-semibold text-gray-800">Tampon de l’entreprise</div>

              <div className="flex items-start gap-5 flex-wrap">
                <div className="flex items-center justify-center rounded-xl border border-gray-200"
                  style={{
                    width: '220px', height: '120px', padding: '10px',
                    // Damier discret : un PNG transparent doit se voir comme tel.
                    backgroundImage: 'linear-gradient(45deg,#f3f4f6 25%,transparent 25%),linear-gradient(-45deg,#f3f4f6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f3f4f6 75%),linear-gradient(-45deg,transparent 75%,#f3f4f6 75%)',
                    backgroundSize: '14px 14px',
                    backgroundPosition: '0 0,0 7px,7px -7px,-7px 0',
                  }}>
                  {tampon == null ? (
                    <span className="text-xs text-gray-400">Chargement…</span>
                  ) : tampon.present ? (
                    <img key={tamponVer} src={tamponSrc || undefined} alt="Tampon de l’entreprise"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onError={() => setTampon(t => ({ ...(t || {}), present: false }))} />
                  ) : (
                    <span className="text-xs text-gray-400 text-center px-3">Aucun tampon enregistré</span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-600">Largeur à l’impression (mm)</label>
                    <input type="number" min="15" max="90" value={tamponLargeur}
                      onChange={e => setTamponLargeur(e.target.value)} placeholder="auto"
                      className="w-32 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
                    <p className="text-[11px] text-gray-400">Entre 15 et 90 mm. Laissez vide pour la taille par défaut.</p>
                  </div>
                  <input ref={tamponFileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; choisirFichier(f); }} />
                  <button onClick={() => tamponFileRef.current?.click()} disabled={tamponBusy}
                    className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                    <Upload className="w-4 h-4" /> {tampon?.present ? 'Remplacer le tampon' : 'Ajouter un tampon'}
                  </button>
                  {tampon?.present && (
                    <button onClick={supprimerTampon} disabled={tamponBusy}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                      <Trash2 className="w-4 h-4" /> Retirer
                    </button>
                  )}
                </div>
              </div>

              {tampon?.present && !brouillon && (
                <div className="text-xs text-gray-500">
                  {tampon.name && <span className="font-mono">{tampon.name}</span>}
                  {tampon.largeur_mm ? <span> · {tampon.largeur_mm} mm</span> : null}
                  {tampon.updated_at ? <span> · mis à jour le {new Date(tampon.updated_at).toLocaleDateString('fr-FR')}</span> : null}
                </div>
              )}

              {/* Détourage : rien n'est envoyé tant que le résultat n'est pas validé à l'œil. */}
              {brouillon && (
                <div className="border border-indigo-200 bg-indigo-50/40 rounded-2xl p-4 space-y-4">
                  <div className="text-sm font-semibold text-gray-800">Avant d’enregistrer — encadrez le tampon</div>

                  {/* Étape 1 : le cadrage. Sur la photo d'un courrier, l'en-tête
                      et le texte imprimé sont de l'encre eux aussi : seul
                      l'utilisateur sait où est son tampon. */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs text-gray-500">
                        Glissez sur la photo pour entourer votre tampon. Tout ce qui est en dehors du cadre est ignoré.
                      </p>
                      <div className="flex items-center gap-2">
                        {zone && (
                          <button onClick={() => setZone(null)}
                            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 transition">
                            Toute la photo
                          </button>
                        )}
                        <button onClick={() => setPlein(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 transition">
                          <Maximize2 className="w-3.5 h-3.5" /> Agrandir
                        </button>
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-2 inline-block max-w-full">
                      <CadreurTampon url={brouillon.urlOrigine} zone={zone} onZone={setZone} hauteur={260} />
                    </div>
                    {!zone && (
                      <p className="text-[11px] text-gray-400">
                        Sans cadre, toute la photo est traitée — y compris le texte imprimé autour du tampon.
                      </p>
                    )}
                  </div>

                  <hr className="border-indigo-100" />

                  <div className="text-sm font-semibold text-gray-800">Vérifiez le fond</div>
                  <p className="text-xs text-gray-500">
                    {detourage?.deja
                      ? 'Cette image est déjà transparente : elle a seulement été recadrée sur le tampon, pour que la marge vide ne compte pas dans la largeur d’impression.'
                      : 'Le fond a été effacé automatiquement et l’image recadrée sur l’encre : sur un contrat, seul le tampon apparaîtra, sans rectangle de papier par-dessus le texte.'}
                    {' '}Les cases grises indiquent ce qui est transparent.
                  </p>

                  <div className="flex gap-4 flex-wrap">
                    {[
                      { cle: 'detoure', titre: detourage?.deja ? 'Recadré' : 'Fond effacé', recommande: true },
                      { cle: 'origine', titre: 'Image d’origine', recommande: false },
                    ].map(opt => {
                      const estDetoure = opt.cle === 'detoure';
                      const indisponible = estDetoure && detourage === 'echec';
                      const actif = estDetoure ? detourActif : !detourActif;
                      const url = estDetoure
                        ? (detourage && detourage !== 'echec' ? detourage.url : null)
                        : (brut ? brut.url : brouillon.urlOrigine);
                      return (
                        <button key={opt.cle} type="button" disabled={indisponible}
                          onClick={() => setDetourActif(estDetoure)}
                          className={`text-left rounded-xl p-2 border-2 transition ${actif && !indisponible ? 'border-indigo-500 bg-white' : 'border-transparent bg-white/60 hover:border-gray-200'} ${indisponible ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <div className="flex items-center justify-center rounded-lg border border-gray-200 relative"
                            style={{
                              width: '190px', height: '110px', padding: '8px',
                              backgroundImage: 'linear-gradient(45deg,#f3f4f6 25%,transparent 25%),linear-gradient(-45deg,#f3f4f6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f3f4f6 75%),linear-gradient(-45deg,transparent 75%,#f3f4f6 75%)',
                              backgroundSize: '14px 14px',
                              backgroundPosition: '0 0,0 7px,7px -7px,-7px 0',
                            }}>
                            {estDetoure && detourEnCours ? (
                              <span className="text-xs text-gray-500">Traitement…</span>
                            ) : indisponible ? (
                              <span className="text-xs text-gray-500 text-center px-2">Fond non reconnu</span>
                            ) : url ? (
                              <img src={url} alt={opt.titre} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            ) : null}
                          </div>
                          <div className="mt-1.5 text-xs font-medium text-gray-700 px-1">
                            {opt.titre}
                            {opt.recommande && <span className="ml-1.5 text-[10.5px] bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">recommandé</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {detourActif && detourage && detourage !== 'echec' && !detourage.deja && (
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Réglage</label>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-400">Garde plus</span>
                        <input type="range" min="-30" max="30" step="5" value={force}
                          onChange={e => setForce(Number(e.target.value))} className="flex-1 max-w-xs" />
                        <span className="text-[11px] text-gray-400">Efface plus</span>
                      </div>
                      <p className="text-[11px] text-gray-400">
                        S’il reste des taches de papier, glissez vers la droite. Si le tampon devient pâle ou troué, glissez vers la gauche.
                      </p>
                    </div>
                  )}

                  {detourage === 'echec' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                      Le fond n’a pas pu être reconnu sur cette image — elle sera enregistrée telle quelle.
                      Une photo du tampon sur une feuille blanche, bien éclairée, donne le meilleur résultat.
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button onClick={enregistrerTampon} disabled={tamponBusy || (detourActif && detourEnCours)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                      <Save className="w-4 h-4" /> {tamponBusy ? 'Envoi…' : 'Enregistrer ce tampon'}
                    </button>
                    <button onClick={annulerBrouillon} disabled={tamponBusy}
                      className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition">
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Plein écran : sur une photo de courrier, le tampon est petit —
                  il faut pouvoir le viser confortablement. Le cadre tracé ici
                  est le même que sur la vignette. */}
              {plein && brouillon && (
                <div onClick={() => setPlein(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.88)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', padding: '20px' }}>
                  <div onClick={e => e.stopPropagation()} className="flex items-center gap-2 flex-wrap justify-center">
                    <span className="text-xs text-white/70">Glissez pour entourer votre tampon</span>
                    {zone && (
                      <button onClick={() => setZone(null)}
                        className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-medium hover:bg-white/20 transition">
                        Toute la photo
                      </button>
                    )}
                    <button onClick={() => setPlein(false)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-800 rounded-lg text-xs font-semibold hover:bg-gray-100 transition">
                      <X className="w-3.5 h-3.5" /> Fermer
                    </button>
                  </div>
                  <div onClick={e => e.stopPropagation()}>
                    <CadreurTampon url={brouillon.urlOrigine} zone={zone} onZone={setZone} hauteur="76vh" />
                  </div>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                Une <strong>photo de votre tampon encré</strong> suffit — même sur un courrier : vous entourez le tampon,
                le reste est ignoré. Le fond est effacé ici, dans votre navigateur, avant l’envoi. Si vous disposez déjà
                d’un PNG à fond transparent, il est utilisé tel quel. PNG ou JPEG, 2 Mo maximum une fois traité.
              </div>
            </div>
          )}

          {/* ── FONCTIONNALITÉS (feature flags org) ── */}
          {section === 'conges' && <TypesCongeSection showNotification={showNotification} />}

          {/* ── RAPPELS D'ENTRETIEN (le mail qui part avant le rendez-vous) ── */}
          {section === 'rappels' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold" style={{ color: 'var(--erp-ink-strong)' }}>Rappels d'entretien</h2>
              <RappelsEntretienSection showNotification={showNotification} />
            </div>
          )}

          {/* ── ENTRETIENS (règle des créneaux proposés) ── */}
          {section === 'entretiens' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold" style={{ color: 'var(--erp-ink-strong)' }}>Entretiens</h2>
              {/* COMMENT L'AGENT MÈNE LES ENTRETIENS TÉLÉPHONIQUES — le choix
                  de version, dont une en bêta. Au-dessus des créneaux : c'est
                  la question qu'on se pose après avoir écouté un appel. */}
              <div>
                <h3 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>
                  Entretiens téléphoniques menés par l'agent
                </h3>
                <AgentVocalSection showNotification={showNotification} />
              </div>
              <EntretiensConfig showNotification={showNotification} />
            </div>
          )}

          {/* ── COÛTS EMPLOYEUR (barème du CJM) ── */}
          {section === 'couts' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold" style={{ color: 'var(--erp-ink-strong)' }}>Coûts employeur</h2>
              <CoutsEmployeur showNotification={showNotification} />
            </div>
          )}

          {section === 'features' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <ToggleLeft className="w-4 h-4 text-indigo-600" /> Fonctionnalités
              </h2>
              <p className="text-sm text-gray-500">Activez ou désactivez les fonctionnalités pour votre organisation. Les écrans et boutons désactivés disparaissent immédiatement de l'interface ; les endpoints correspondants sont aussi bloqués côté serveur. Les modules (CRM, timesheet, signature, NDA, LinkedIn…) s'activent et se règlent dans leur page du <strong>Marketplace</strong>.</p>

              <div className="space-y-2">
                {features == null ? (
                  <div className="text-sm text-gray-400">Chargement…</div>
                ) : Object.keys(features).length === 0 ? (
                  <div className="text-sm text-gray-400">Aucun flag renvoyé par le serveur.</div>
                ) : Object.entries(features).filter(([key]) => !CLES_MODULES.includes(key)).map(([key, f]) => {
                  const enabled = !f || f.enabled !== false;
                  const busy = flagSaving === key;
                  return (
                    <div key={key} className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-800 text-sm">{f?.label || key}</span>
                        <span className="ml-2 text-[11px] text-gray-400 font-mono">{key}</span>
                        {f?.known === false && <span className="ml-2 text-[10.5px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">clé libre</span>}
                        {!enabled && <span className="ml-2 text-[10.5px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">masqué pour les utilisateurs</span>}
                        {!enabled && <p className="text-[11px] text-gray-400 mt-0.5">Coupé pour les utilisateurs — vous (admin) y accédez toujours.</p>}
                      </div>
                      <button onClick={() => toggleFlag(key, !enabled)} disabled={busy}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-indigo-600' : 'bg-gray-200'} ${busy ? 'opacity-50' : ''}`}
                        title={enabled ? 'Désactiver' : 'Activer'}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Créer un flag personnalisé (front-only)</label>
                <div className="flex gap-2">
                  <input value={newFlagKey} onChange={e => setNewFlagKey(e.target.value)} placeholder="ex. ecran_marketplace"
                    onKeyDown={e => { if (e.key === 'Enter') addCustomFlag(); }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
                  <button onClick={addCustomFlag} disabled={!newFlagKey.trim() || flagSaving != null}
                    className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                    <Plus className="w-4 h-4" /> Créer
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">Toute clé inconnue du backend est acceptée et resservie par le GET — utile pour piloter vos propres écrans.</p>
              </div>
            </div>
          )}

          {/* ── RÉFÉRENTIELS (bases de connaissances) ── */}
          {section === 'referentiels' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Library className="w-4 h-4 text-indigo-600" /> Référentiels
              </h2>
              <p className="text-sm text-gray-500">Bases de connaissances de votre organisation, regroupées ici. D'autres référentiels viendront s'ajouter à cette page.</p>

              {/* Sous-section : Certifications */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">Certifications</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Détection des anomalies de CV (ex. version d'une certification). Ajoutez une version (« v5 » sur ITIL) dès sa sortie.</p>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2.5 py-1 flex-none">{Array.isArray(certs) ? certs.length : '…'}</span>
                </div>
                <div className="p-4 space-y-3">
                  <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <input value={certForm.nom} onChange={e => setCertForm(f => ({ ...f, nom: e.target.value }))} placeholder="Nom (ex. ITIL)"
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <input value={certForm.versions} onChange={e => setCertForm(f => ({ ...f, versions: e.target.value }))} placeholder="Versions (ex. v3, v4, v5)"
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <input value={certForm.notes} onChange={e => setCertForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes"
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <button onClick={saveCert} disabled={certSaving || !certForm.nom.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition">
                      <Save className="w-4 h-4" /> {certSaving ? 'Enregistrement…' : (certForm.nom && Array.isArray(certs) && certs.some(c => c.nom === certForm.nom) ? 'Mettre à jour' : 'Enregistrer la certification')}
                    </button>
                  </div>

                  {/* Barre de recherche */}
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={certQuery} onChange={e => setCertQuery(e.target.value)} placeholder="Rechercher une certification, une version…"
                      className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-indigo-400" />
                  </div>

                  <div className="space-y-2">
                    {certs == null ? (
                      <div className="text-sm text-gray-400">Chargement…</div>
                    ) : certs.length === 0 ? (
                      <div className="text-sm text-gray-400">Aucune certification enregistrée.</div>
                    ) : certsFiltered.length === 0 ? (
                      <div className="text-sm text-gray-400">Aucun résultat pour « {certQuery} ».</div>
                    ) : certsFiltered.map((c, i) => (
                      <div key={c.id ?? i} className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2.5">
                        <span className="font-semibold text-gray-800 text-sm">{c.nom}</span>
                        <div className="flex flex-wrap gap-1.5">
                          {(c.versions || []).map((v, j) => <span key={j} className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5">{v}</span>)}
                        </div>
                        {c.notes && <span className="text-xs text-gray-400 ml-auto truncate" style={{ maxWidth: '240px' }}>{c.notes}</span>}
                        <button onClick={() => setCertForm({ nom: c.nom || '', versions: (c.versions || []).join(', '), notes: c.notes || '' })}
                          className="text-xs text-indigo-600 font-medium hover:underline flex-none ml-auto">Éditer</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── CONSOMMATION IA (visible seulement si le serveur l'autorise) ── */}
          {section === 'conso_ia' && consoIA && (
            <ConsommationIA initial={consoIA} showNotification={showNotification} />
          )}

          {/* ── DIAGNOSTIC BOOND ── */}
          {section === 'boond' && (
            <div className="space-y-6">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Plug className="w-4 h-4" /> Diagnostic Boond
              </h2>
              <p className="text-sm text-gray-500">Outils de lecture seule pour diagnostiquer la connexion et l'API Boond. Aucune écriture.</p>

              {/* 1. Ping */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Connexion</h3>
                <button onClick={testBoondPing} disabled={boondPinging}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition disabled:opacity-50">
                  <i className={`bi ${boondPinging ? 'bi-arrow-repeat animate-spin' : 'bi-plug'}`} /> {boondPinging ? 'Test…' : 'Tester la connexion Boond'}
                </button>
                {boondPing && (
                  boondPing.ok ? (
                    <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-green-50 text-green-700">
                      <i className="bi bi-check-circle-fill" /> Connexion OK{boondPing.sample != null ? ` · ${boondPing.sample} candidat(s) échantillon` : ''}
                    </div>
                  ) : (
                    <div className="text-sm px-3 py-2 rounded-lg bg-red-50 text-red-600 break-words font-mono">{boondPing.error}</div>
                  )
                )}
              </div>

              {/* 2. Probe */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Explorer un candidat</h3>
                <p className="text-xs text-gray-400">Référence Boond (ex. celle de la fiche candidat) ou identifiant numérique.</p>
                <div className="flex gap-2">
                  <input value={boondRef} onChange={e => setBoondRef(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') probeBoond(); }}
                    placeholder="Référence ou ID Boond…"
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gray-300 font-mono" />
                  <button onClick={() => probeBoond()} disabled={boondProbing || !boondRef.trim()}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition disabled:opacity-50">
                    <i className={`bi ${boondProbing ? 'bi-arrow-repeat animate-spin' : 'bi-search'}`} /> {boondProbing ? 'Sonde…' : 'Sonder'}
                  </button>
                </div>
                {boondProbe && (
                  <div className="relative">
                    <button onClick={copyProbe} className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                      <i className="bi bi-clipboard" /> Copier
                    </button>
                    <pre className="text-xs bg-gray-900 text-gray-100 rounded-xl p-4 overflow-auto max-h-[420px] font-mono leading-relaxed">{JSON.stringify(boondProbe, null, 2)}</pre>
                  </div>
                )}
              </div>

              {/* 3. Probe-list (liste des candidats Boond) */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-700">Lister les candidats Boond</h3>
                  <button onClick={listBoond} disabled={boondListing}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition disabled:opacity-50">
                    <i className={`bi ${boondListing ? 'bi-arrow-repeat animate-spin' : 'bi-list-ul'}`} /> {boondListing ? 'Chargement…' : 'Lister'}
                  </button>
                </div>
                <p className="text-xs text-gray-400">Renvoie les candidats vus par l'API Boond (lecture seule). Cliquer une ligne la charge dans « Explorer un candidat ».</p>
                {boondList && (() => {
                  if (boondList.error) return <div className="text-sm px-3 py-2 rounded-lg bg-red-50 text-red-600 break-words font-mono">{boondList.error}</div>;
                  const rows = Array.isArray(boondList) ? boondList
                    : (boondList.candidates || boondList.data || boondList.items || boondList.rows || boondList.results || []);
                  const idOf = (r) => r.candidateId ?? r.candidate_id ?? r.id ?? r.boond_id ?? r.boondId ?? null;
                  const refOf = (r) => r.reference ?? r.ref ?? r.boond_ref ?? null;
                  const nameOf = (r) => r.name || r.fullName || `${r.firstName || r.first_name || ''} ${r.lastName || r.last_name || ''}`.trim() || '—';
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{Array.isArray(rows) ? `${rows.length} candidat(s)` : 'Réponse non tabulaire'}</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setBoondListRaw(v => !v)} className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                            <i className="bi bi-code-slash" /> {boondListRaw ? 'Vue liste' : 'JSON brut'}
                          </button>
                          <button onClick={copyList} className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                            <i className="bi bi-clipboard" /> Copier
                          </button>
                        </div>
                      </div>
                      {boondListRaw || !Array.isArray(rows) ? (
                        <pre className="text-xs bg-gray-900 text-gray-100 rounded-xl p-4 overflow-auto max-h-[420px] font-mono leading-relaxed">{JSON.stringify(boondList, null, 2)}</pre>
                      ) : rows.length === 0 ? (
                        <div className="text-sm px-3 py-2 rounded-lg bg-gray-50 text-gray-500">Aucun candidat renvoyé.</div>
                      ) : (
                        <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-50 max-h-[420px] overflow-y-auto">
                          {rows.map((r, i) => {
                            const id = idOf(r); const ref = refOf(r);
                            return (
                              <button key={i} onClick={() => probeBoond(id ?? ref)} disabled={id == null && ref == null}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 disabled:opacity-50">
                                <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold flex-none">{(nameOf(r)[0] || '?').toUpperCase()}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-800 truncate">{nameOf(r)}</div>
                                  <div className="text-xs text-gray-400 font-mono truncate">{[ref ? `ref ${ref}` : null, id != null ? `id ${id}` : null].filter(Boolean).join(' · ') || '—'}</div>
                                </div>
                                <i className="bi bi-search text-gray-400 flex-none" />
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* 4. Import des candidats Boond (test avec limite) */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Importer les candidats Boond</h3>
                <p className="text-xs text-gray-400">Crée / met à jour les candidats dans l'ERP et importe leurs CV, pièces & activités. Idempotent. Teste d'abord avec une limite.</p>
                {(() => {
                  const running = !!boondImport?.running;
                  const total = boondImport?.total || 0;
                  const done = boondImport?.done || 0;
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  return (
                    <>
                      <div className="flex items-end gap-2 flex-wrap">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Limite (test)</label>
                          <input type="number" min="0" value={boondImportLimit} onChange={e => setBoondImportLimit(e.target.value)}
                            placeholder="ex. 10" disabled={running || boondImportStarting}
                            className="w-28 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gray-300 font-mono" />
                        </div>
                        <button onClick={runBoondImport} disabled={running || boondImportStarting}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition disabled:opacity-50">
                          <i className={`bi ${running || boondImportStarting ? 'bi-arrow-repeat animate-spin' : 'bi-cloud-download'}`} /> {running ? 'Import en cours…' : boondImportStarting ? 'Lancement…' : 'Importer les candidats Boond'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400">Vide ou 0 = import complet (~2102 candidats), avec confirmation.</p>

                      {running && (
                        <div>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5"><span>Synchronisation depuis Boond…</span><span className="font-mono">{done}/{total}</span></div>
                          <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                        </div>
                      )}

                      {boondImport && !running && (boondImport.done != null || boondImport.created != null || boondImport.resumes_ok != null) && (
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2.5 py-1 rounded-lg bg-green-50 text-green-700 font-medium">{boondImport.created ?? 0} créé(s)</span>
                          <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-medium">{boondImport.updated ?? 0} mis à jour</span>
                          <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 font-medium">{boondImport.resumes_ok ?? 0} CV · {boondImport.files_ok ?? 0} pièce(s)</span>
                          <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 font-medium">{boondImport.activities_ok ?? 0} activité(s)</span>
                          {(boondImport.failed ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-red-50 text-red-600 font-medium">{boondImport.failed} échec(s)</span>}
                          {(boondImport.skipped ?? 0) > 0 && <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-500 font-medium">{boondImport.skipped} ignoré(s)</span>}
                        </div>
                      )}

                      {!running && boondImport?.errors?.length > 0 && (
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-100">Échecs ({boondImport.errors.length})</div>
                          <div className="max-h-44 overflow-auto divide-y divide-gray-50">
                            {boondImport.errors.map((e, i) => (
                              <div key={i} className="px-3 py-2 text-xs flex items-center gap-2">
                                <span className="font-medium text-gray-700 truncate" style={{ maxWidth: '180px' }}>{e.boond_ref ? `Boond ${e.boond_ref}` : `#${e.candidate_id}`}</span>
                                <span className="text-red-500 truncate flex-1">{e.error}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Modèle général de flow de recrutement (étapes) ─────────────────────────
function FlowTemplate({ showNotification }) {
  const DEFAULT = [
    { id: 'brief', label: 'Brief' },
    { id: 'sourcing', label: 'Sourcing' },
    { id: 'qualif', label: 'Qualification' },
    { id: 'entretien_client', label: 'Entretien client' },
    { id: 'proposition', label: 'Proposition' },
    { id: 'decision', label: 'Décision' },
  ];
  const [stages, setStages] = useState(DEFAULT);
  const [newLabel, setNewLabel] = useState('');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/config/recruitment-flow`, { headers });
        if (res.ok) { const d = await res.json(); if (d.stages?.length) { setStages(d.stages); return; } }
      } catch {}
      try { const local = JSON.parse(localStorage.getItem('recruitment_flow')); if (local?.length) setStages(local); } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (next) => {
    setStages(next);
    try {
      const res = await fetch(`${BASE}/config/recruitment-flow`, { method: 'PUT', headers, body: JSON.stringify({ stages: next }) });
      if (!res.ok) throw new Error();
      showNotification?.('Modèle de flow enregistré', 'success');
    } catch { localStorage.setItem('recruitment_flow', JSON.stringify(next)); showNotification?.('Flow enregistré (local)', 'success'); }
  };
  const add = () => { if (!newLabel.trim()) return; const id = newLabel.toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + Date.now(); save([...stages, { id, label: newLabel.trim() }]); setNewLabel(''); };
  const rename = (i, label) => setStages(s => s.map((x, k) => k === i ? { ...x, label } : x));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= stages.length) return; const n = [...stages]; [n[i], n[j]] = [n[j], n[i]]; save(n); };
  const remove = (i) => save(stages.filter((_, k) => k !== i));

  const box = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px' };
  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <h2 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: 'var(--text-strong)' }}>Flow de recrutement — modèle général</h2>
      <p style={{ margin: '0 0 18px', fontSize: '13.5px', color: 'var(--text-2)' }}>
        Étapes appliquées par défaut à chaque besoin. Chaque besoin pourra ensuite personnaliser ses propres étapes (à venir).
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
        {stages.map((s, i) => (
          <div key={s.id} style={{ ...box, display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px' }}>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-faint)', width: '20px', flex: 'none' }}>{i + 1}</span>
            <input value={s.label} onChange={e => rename(i, e.target.value)} onBlur={() => save(stages)}
              onKeyDown={e => { if (e.key === 'Enter') save(stages); }}
              style={{ flex: 1, minWidth: 0, border: '1px solid transparent', background: 'transparent', color: 'var(--text-strong)', fontSize: '14px', fontWeight: 500, fontFamily: 'inherit', borderRadius: '7px', padding: '5px 8px', outline: 'none' }}
              onFocus={e => { e.target.style.border = '1px solid var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }}
              onBlurCapture={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }} />
            <button onClick={() => move(i, -1)} disabled={i === 0} title="Monter" style={{ background: 'transparent', border: 'none', color: 'var(--text-3)', cursor: i === 0 ? 'not-allowed' : 'pointer', opacity: i === 0 ? 0.3 : 1, padding: '4px', fontSize: '13px' }}><i className="bi bi-chevron-up" /></button>
            <button onClick={() => move(i, 1)} disabled={i === stages.length - 1} title="Descendre" style={{ background: 'transparent', border: 'none', color: 'var(--text-3)', cursor: i === stages.length - 1 ? 'not-allowed' : 'pointer', opacity: i === stages.length - 1 ? 0.3 : 1, padding: '4px', fontSize: '13px' }}><i className="bi bi-chevron-down" /></button>
            <button onClick={() => remove(i)} title="Supprimer" style={{ background: 'transparent', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', padding: '4px', fontSize: '13px' }}><i className="bi bi-trash3" /></button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="Nouvelle étape…" style={{ flex: 1, maxWidth: '320px', padding: '9px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '13.5px', fontFamily: 'inherit', outline: 'none' }} />
        <button onClick={add} disabled={!newLabel.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 16px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontSize: '13.5px', fontWeight: 600, fontFamily: 'inherit', cursor: newLabel.trim() ? 'pointer' : 'not-allowed', opacity: newLabel.trim() ? 1 : 0.5 }}>
          <i className="bi bi-plus-lg" /> Ajouter l'étape
        </button>
      </div>
    </div>
  );
}

// ── Template du CV format entreprise (admin) ─────────────────────────────────
// Même mécanique que le template NDA : GET/POST/DELETE /config/cv/template.
// Le but est de rendre la mise en page autonome — récupérer le modèle, le
// corriger dans Word (marges, pied de page…), le renvoyer — sans passer par
// une intervention serveur à chaque retouche.
export function CvTemplateCard({ showNotification }) {
  const [tpl, setTpl] = React.useState(undefined); // undefined = chargement, null = route absente
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);
  const authHdr = { Authorization: `Bearer ${APIService.getToken()}` };

  const load = React.useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/cv/template`, { headers: authHdr });
      if (r.status === 404 || r.status === 501) { setTpl(null); return; }
      if (!r.ok) { setTpl(null); return; }
      const j = await r.json().catch(() => ({}));
      setTpl(j.data || j);
    } catch { setTpl(null); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.docx$/i.test(file.name)) { showNotification?.('Le modèle doit être un fichier .docx.', 'error'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('template', file);
      const r = await fetch(`${BASE}/config/cv/template`, { method: 'POST', headers: authHdr, body: fd });
      if (r.status === 404 || r.status === 501) { showNotification?.('Le dépôt du modèle de CV arrive côté serveur — rien n\'a été enregistré.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Modèle de CV remplacé — les prochains CV générés l\'utiliseront.', 'success');
      load();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const telecharger = async () => {
    try {
      const r = await fetch(`${BASE}/config/cv/template?download=1`, { headers: authHdr });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = tpl?.name || tpl?.file_name || 'modele-cv-entreprise.docx'; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { showNotification?.('Téléchargement impossible : ' + e.message, 'error'); }
  };

  const revenirDefaut = async () => {
    if (!await confirmErpia({ message: 'Revenir au modèle de CV par défaut ? Le modèle de votre organisation sera retiré.' })) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/config/cv/template`, { method: 'DELETE', headers: authHdr });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Retour au modèle par défaut.', 'success');
      load();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const perso = tpl && tpl.is_default === false;
  const vars = Array.isArray(tpl?.variables) ? tpl.variables : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="w-4 h-4 text-indigo-600" />
        <h2 className="text-base font-bold text-gray-800">Modèle du CV format entreprise</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        La mise en page des CV générés (marges, en-tête, pied de page, polices) vient de ce modèle Word.
        Récupérez-le, corrigez-le dans Word, renvoyez-le : les CV suivants l'utiliseront.
      </p>

      <input ref={fileRef} type="file" accept=".docx" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }} />

      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 flex-wrap">
        <FileText className="w-5 h-5 text-gray-500 flex-none" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-gray-800">
            {tpl === undefined ? 'Chargement…' : perso ? (tpl.name || tpl.file_name || 'Modèle de votre organisation') : 'Modèle par défaut'}
          </p>
          <p className="text-[11.5px] text-gray-500 mt-0.5">
            {tpl === null
              ? 'La gestion du modèle de CV arrive côté serveur — le modèle par défaut est utilisé en attendant.'
              : perso
                ? `Modèle de votre organisation${tpl.updated_at ? ` · mis à jour le ${new Date(tpl.updated_at).toLocaleDateString('fr-CH')}` : ''}`
                : tpl === undefined ? '' : 'Aucun modèle personnalisé — le modèle fourni est utilisé.'}
          </p>
        </div>
        {tpl !== null && tpl !== undefined && (
          <button onClick={telecharger} disabled={busy}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50">
            Télécharger le modèle
          </button>
        )}
        <button onClick={() => fileRef.current?.click()} disabled={busy || tpl === null}
          className="px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-black disabled:opacity-50">
          {busy ? 'Envoi…' : perso ? 'Remplacer' : 'Déposer un modèle'}
        </button>
        {perso && (
          <button onClick={revenirDefaut} disabled={busy}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50">
            Revenir au défaut
          </button>
        )}
      </div>

      {/* Les variables ne sont affichées que si le serveur les publie : en
          inventer conduirait à écrire dans le modèle des champs jamais
          remplacés. */}
      {vars && vars.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Variables remplacées à la génération</p>
          <div className="flex flex-wrap gap-1.5">
            {vars.map((v, i) => (
              <code key={i} className="text-[11.5px] px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono">
                {typeof v === 'string' ? v : (v.cle || v.nom || JSON.stringify(v))}
              </code>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11.5px] text-gray-400 mt-3 leading-relaxed">
        Conseil de mise en page : la marge basse doit être au moins aussi haute que le pied de page,
        sinon le texte passe dessous et se superpose au bloc de contact.
      </p>
    </div>
  );
}

// ── Templates (admin) : contrats DOCX & emails ───────────────────────────────
// GET /templates?type=contrat|nda|email → { data: [{ id, type, nom, file_name,
// variables[], has_file, subject, updated_at }] } ; POST multipart `template`
// (.docx) + nom, ou JSON { type:'email', nom, subject, body } ; GET
// /templates/:id?download=1 ; PUT (renommer/remplacer) ; DELETE. Les
// variables détectées s'affichent sous chaque template.
export function TemplatesSection({ showNotification }) {
  const [type, setType] = React.useState('contrat');
  // Les modèles portés par un fichier Word : contrats ET NDA (24.09 : « je
  // veux un NDA 3 mois et un NDA 6 mois »). Même dépôt, même aperçu ; le NDA
  // se CHOISIT ensuite au moment de l'envoyer.
  const estWord = type === 'contrat' || type === 'nda';
  const LIBELLE = { contrat: 'de contrat', nda: 'de NDA', email: 'd\'email' };
  const [list, setList] = React.useState(null);   // null = chargement
  const [missing, setMissing] = React.useState(false);
  // Un REFUS (réservé aux administrateurs) n'est pas une liste vide : le
  // montrer comme « aucun modèle » faisait créer un modèle… refusé au clic.
  const [refus, setRefus] = React.useState(null);
  const [form, setForm] = React.useState(null);    // création
  const [editId, setEditId] = React.useState(null); // édition email / renommage
  const [busy, setBusy] = React.useState(false);
  const replaceRef = React.useRef({});
  const authHdr = { Authorization: `Bearer ${APIService.getToken()}` };
  const jsonHdr = { ...authHdr, 'Content-Type': 'application/json' };

  const load = React.useCallback(async () => {
    setList(null); setMissing(false); setRefus(null);
    try {
      const r = await fetch(`${BASE}/templates?type=${type}`, { headers: authHdr });
      if (r.status === 404 || r.status === 501) { setMissing(true); setList([]); return; }
      const j = await r.json().catch(() => ({}));
      if (r.status === 403) { setRefus(j.error || 'Réservé aux administrateurs de l\'organisation.'); setList([]); return; }
      const arr = j.data || (Array.isArray(j) ? j : []);
      setList(Array.isArray(arr) ? arr : []);
    } catch { setList([]); }
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form?.nom?.trim()) { showNotification('Le nom est requis.', 'error'); return; }
    setBusy(true);
    try {
      let r;
      if (estWord) {
        if (!form.file) { showNotification('Choisissez un fichier .docx.', 'error'); setBusy(false); return; }
        const fd = new FormData();
        fd.append('template', form.file);
        fd.append('nom', form.nom.trim());
        fd.append('type', type);
        r = await fetch(`${BASE}/templates`, { method: 'POST', headers: authHdr, body: fd });
      } else {
        r = await fetch(`${BASE}/templates`, { method: 'POST', headers: jsonHdr, body: JSON.stringify({ type: 'email', nom: form.nom.trim(), subject: form.subject || '', body: form.body || '' }) });
      }
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 201 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification(`Template « ${form.nom} » créé.`, 'success');
      setForm(null); load();
    } catch (e) { showNotification('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const saveEdit = async (t, payload, isFile) => {
    setBusy(true);
    try {
      let r;
      if (isFile) {
        const fd = new FormData();
        fd.append('template', payload);
        r = await fetch(`${BASE}/templates/${t.id}`, { method: 'PUT', headers: authHdr, body: fd });
      } else {
        r = await fetch(`${BASE}/templates/${t.id}`, { method: 'PUT', headers: jsonHdr, body: JSON.stringify(payload) });
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification('Template mis à jour.', 'success');
      setEditId(null); load();
    } catch (e) { showNotification('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const download = async (t) => {
    try {
      const r = await fetch(`${BASE}/templates/${t.id}?download=1`, { headers: authHdr });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = t.file_name || `${t.nom}.docx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { showNotification('Téléchargement impossible : ' + e.message, 'error'); }
  };

  const remove = async (t) => {
    if (!await confirmErpia({ message: `Supprimer le template « ${t.nom} » ?` })) return;
    try {
      const r = await fetch(`${BASE}/templates/${t.id}`, { method: 'DELETE', headers: authHdr });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification('Template supprimé.', 'success');
      load();
    } catch (e) { showNotification('Erreur : ' + e.message, 'error'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-4 h-4 text-indigo-600" /> Templates
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
            {[['contrat', 'Contrats (DOCX)'], ['nda', 'NDA (DOCX)'], ['email', 'Emails']].map(([k, l]) => (
              <button key={k} onClick={() => { setType(k); setForm(null); setEditId(null); }}
                className={`px-3 py-2 ${type === k ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
          {!refus && (
            <button onClick={() => setForm(estWord ? { nom: '', file: null } : { nom: '', subject: '', body: '' })}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-black">
              <Plus className="w-4 h-4" /> Nouveau template
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-500">
        {type === 'nda'
          ? 'Modèles Word de NDA — un par variante (NDA 3 mois, NDA 6 mois…). Variables {{candidat}}, {{candidat_prenom}}, {{candidat_nom}}, {{client}}, {{societe}}, {{date}}. Le modèle se choisit à l\'envoi du NDA, et son texte se relit avant de partir.'
          : type === 'contrat'
          ? 'Templates DOCX des contrats — variables {{cle}} ou $cle$ (les modèles Word exportés type $firstName$ marchent tels quels), remplacées à la génération (Dossier AO → Générer les contrats).'
          : 'Templates d\'emails — objet + corps, variables {{…}} remplacées à l\'envoi.'}
      </p>

      {/* Le refus du serveur, TEL QUEL : il dit quel compte, quel rôle, et
          où le changer. Une liste vide à la place faisait créer un modèle
          que le clic suivant refusait. */}
      {refus && (
        <div role="alert" className="erp-card p-3 text-sm" style={{ color: 'var(--erp-danger)' }}>
          {refus}
        </div>
      )}

      {missing && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-500">
          La route /templates n'est pas encore en ligne — l'écran est prêt, vos templates apparaîtront dès son déploiement.
        </div>
      )}

      {form && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white">
          <p className="text-sm font-semibold text-gray-800">{`Nouveau modèle ${LIBELLE[type] || ''}`}</p>
          <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Nom du template *" autoFocus
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400" />
          {estWord ? (
            <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-600 cursor-pointer hover:bg-gray-50">
              <Upload className="w-4 h-4 text-gray-400" />
              {form.file ? <span className="font-medium text-gray-800">{form.file.name}</span> : 'Choisir le fichier .docx…'}
              <input type="file" accept=".docx" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f && !/\.docx$/i.test(f.name)) { showNotification('Seul le format .docx est accepté.', 'error'); } else if (f) setForm(p => ({ ...p, file: f })); e.target.value = ''; }} />
            </label>
          ) : (
            <>
              <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Objet de l'email"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400" />
              <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={6} placeholder={'Corps de l\'email — variables {{candidat_prenom}}, {{client}}…'}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 font-mono" />
            </>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setForm(null)} className="px-4 py-2 text-sm font-medium text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-100">Annuler</button>
            <button onClick={create} disabled={busy} className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-black disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {busy ? 'Création…' : 'Créer'}
            </button>
          </div>
        </div>
      )}

      {list === null ? (
        <p className="text-sm text-gray-400 py-6 text-center">Chargement…</p>
      ) : list.length === 0 && !form ? (
        <p className="text-sm text-gray-400 py-8 text-center">Aucun modèle {LIBELLE[type] || ''} — créez le premier.</p>
      ) : (
        <div className="space-y-2">
          {list.map(t => (
            <div key={t.id} className="border border-gray-200 rounded-xl p-4 bg-white">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center flex-none"><FileText className="w-4 h-4" /></span>
                <div className="flex-1 min-w-[160px]">
                  {editId === t.id ? (
                    <input defaultValue={t.nom} autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(t, { nom: e.currentTarget.value.trim() }); if (e.key === 'Escape') setEditId(null); }}
                      onBlur={e => { const v = e.currentTarget.value.trim(); if (v && v !== t.nom) saveEdit(t, { nom: v }); else setEditId(null); }}
                      className="text-sm font-semibold border border-indigo-300 rounded-lg px-2 py-1 outline-none w-full" />
                  ) : (
                    <p className="text-sm font-semibold text-gray-800 cursor-pointer" title="Cliquer pour renommer" onClick={() => setEditId(t.id)}>{t.nom}</p>
                  )}
                  <p className="text-[11px] text-gray-400">
                    {t.type === 'email' ? (t.subject ? `Objet : ${t.subject}` : 'Sans objet') : (t.file_name || (t.has_file ? 'DOCX' : 'sans fichier'))}
                    {t.updated_at ? ` · ${new Date(t.updated_at).toLocaleDateString('fr-CH')}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {t.type !== 'email' && t.has_file !== false && (
                    <button onClick={() => download(t)} title="Télécharger le DOCX"
                      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"><Download className="w-3.5 h-3.5" /> DOCX</button>
                  )}
                  {t.type !== 'email' && (
                    <>
                      <input ref={el => { replaceRef.current[t.id] = el; }} type="file" accept=".docx" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) saveEdit(t, f, true); e.target.value = ''; }} />
                      <button onClick={() => replaceRef.current[t.id]?.click()} title="Remplacer le fichier DOCX"
                        className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"><Upload className="w-3.5 h-3.5" /> Remplacer</button>
                    </>
                  )}
                  <button onClick={() => remove(t)} title="Supprimer"
                    className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {Array.isArray(t.variables) && t.variables.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {t.variables.map(v => (
                    <code key={v} className="text-[10.5px] font-mono font-semibold bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">{String(v).replace(/^\{+|\}+$/g, '').replace(/^\$|\$$/g, '')}</code>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── RAPPELS D'ENTRETIEN ──────────────────────────────────────────────────────
// Un entretien confirmé la semaine dernière est un entretien oublié le jour
// même. Le rappel part quelques heures avant, au recruteur qui l'a organisé et
// au candidat qui doit s'y rendre.
//
// Deux interrupteurs SÉPARÉS : se rappeler soi-même n'engage que soi, écrire
// au candidat sort de la maison. L'écran le dit au lieu de les mélanger dans
// une seule case.
/**
 * QUELLE VERSION D'AGENT MÈNE LES ENTRETIENS TÉLÉPHONIQUES.
 *
 * Demande 22.09 : « un truc plus humain, V2 — et ne le mets pas par défaut,
 * mets le choix en mode bêta ».
 *
 * Le catalogue vient du SERVEUR, libellés compris : l'écran n'écrit le nom
 * d'aucune version. Le jour où l'une change de nom ou passe hors bêta, ça se
 * décide à un seul endroit.
 *
 * Une bêta s'annonce COMME UNE BÊTA — et on dit quoi faire avec : l'écouter
 * sur quelques entretiens avant de la laisser à toute l'agence. Un badge seul
 * ne dit pas quel risque on prend.
 */
export function AgentVocalSection({ showNotification }) {
  const [etat, setEtat] = useState(null);   // null = chargement | { ... } | { absent } | { erreur }
  const [busy, setBusy] = useState(null);   // clé en cours d'enregistrement
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}` };

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/agent-vocal`, { headers });
        if ([404, 405, 501].includes(r.status)) { setEtat({ absent: true }); return; }
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
        setEtat(j);
      } catch (e) { setEtat({ erreur: e.message }); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const choisir = async (cle) => {
    setBusy(cle);
    try {
      const r = await fetch(`${BASE}/config/agent-vocal`, { method: 'PUT', headers, body: JSON.stringify({ version: cle }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification(j.error || `HTTP ${r.status}`, 'error'); return; }
      setEtat(e => ({ ...e, version: j.version,
        // Le compte d'appels déjà planifiés voyage avec la réponse : sans ça
        // l'avertissement resterait figé sur la valeur du chargement.
        ...(j.appels_planifies !== undefined ? { appels_planifies: j.appels_planifies } : {}) }));
      // Le message du serveur fait foi : c'est lui qui prévient qu'une bêta
      // est une bêta.
      showNotification(j.message || 'Version enregistrée.', 'success');
    } catch (e) { showNotification(`Enregistrement impossible : ${e.message}`, 'error'); }
    finally { setBusy(null); }
  };

  if (etat == null) return <p style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Lecture du réglage…</p>;
  if (etat.absent) return (
    <div style={{ padding: '12px 14px', borderRadius: '10px', background: 'var(--chip-bg)', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.55 }}>
      Le choix de la version arrive côté serveur. Les entretiens continuent d'être menés comme aujourd'hui.
    </div>
  );
  if (etat.erreur) return <p style={{ fontSize: '12.5px', color: 'var(--erp-danger)' }}>{etat.erreur}</p>;

  return (
    <div style={{ display: 'grid', gap: '10px' }}>
      <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.55, maxWidth: '68ch' }}>
        La façon dont l'agent mène les entretiens téléphoniques. Les questions, le consentement
        à l'enregistrement et la clôture sont les mêmes dans tous les cas — c'est le TON qui change.
        Le choix vaut pour toute l'agence.
      </p>
      {/* CE QUE LE CHANGEMENT EMPORTE, DIT AVANT LE CLIC. La version n'est pas
          figée à la planification : elle est lue au moment où l'appel part.
          C'est voulu (un correctif rattrape tout de suite ce qui est au
          planning) — mais découvrir après coup qu'une bêta a mené l'entretien
          d'une collègue est exactement ce qui fait perdre confiance. */}
      {etat.avertissement_planifies && (
        <p style={{ margin: 0, padding: '9px 12px', borderRadius: '9px', fontSize: '12px', lineHeight: 1.5,
          border: '1px solid var(--erp-warning)', background: 'var(--erp-warning-soft)',
          color: 'var(--erp-warning)', maxWidth: '68ch' }}>
          <i className="bi bi-exclamation-triangle" style={{ marginRight: '6px' }} />
          {etat.avertissement_planifies}
        </p>
      )}
      {(etat.versions || []).map(v => {
        const active = etat.version === v.cle;
        return (
          <button key={v.cle} type="button" onClick={() => !active && choisir(v.cle)} disabled={busy != null}
            style={{ textAlign: 'left', display: 'grid', gap: '4px', padding: '13px 15px', borderRadius: '12px',
              border: `1px solid ${active ? 'var(--brand)' : 'var(--hair)'}`,
              background: active ? 'var(--erp-accent-soft)' : 'var(--panel)',
              cursor: active || busy != null ? 'default' : 'pointer', font: 'inherit', color: 'inherit',
              opacity: busy != null && busy !== v.cle ? 0.6 : 1 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <i className={`bi bi-${active ? 'check-circle-fill' : 'circle'}`} style={{ fontSize: '12px', color: active ? 'var(--brand)' : 'var(--text-faint)' }} />
              <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-strong)' }}>{v.libelle}</span>
              {v.beta && (
                <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                  padding: '2px 7px', borderRadius: '5px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)' }}>bêta</span>
              )}
              {v.defaut && !active && (
                <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>en service par défaut</span>
              )}
            </span>
            <span style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>{v.resume}</span>
            {v.detail && <span style={{ fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>{v.detail}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function RappelsEntretienSection({ showNotification }) {
  const [conf, setConf] = useState(null);      // null = chargement
  const [bornes, setBornes] = useState({ min: 0.5, max: 72 });
  const [absent, setAbsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [journal, setJournal] = useState(null);
  const [modeles, setModeles] = useState([]);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}` };

  const charger = async () => {
    try {
      const r = await fetch(`${BASE}/config/rappels-entretien`, { headers });
      if ([404, 405, 501].includes(r.status)) { setAbsent(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setConf(j.data);
      if (j.min_heures != null) setBornes({ min: j.min_heures, max: j.max_heures });
    } catch (e) { showNotification(`Réglage illisible : ${e.message}`, 'error'); }
  };
  useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Le journal répond à « est-ce que le rappel est bien parti ? » — et surtout,
  // quand il n'est pas parti, POURQUOI. C'est la seule partie sur laquelle
  // quelqu'un peut agir.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/rappels-entretien/journal?limite=20`, { headers });
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        if (Array.isArray(j.data)) setJournal(j.data);
      } catch { /* l'écran reste utilisable sans le journal */ }
    })();
    (async () => {
      try {
        const r = await fetch(`${BASE}/email-templates`, { headers });
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        const arr = j.data || (Array.isArray(j) ? j : []);
        if (Array.isArray(arr)) setModeles(arr);
      } catch { /* le texte par défaut suffit */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enregistrer = async (patch) => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/config/rappels-entretien`, { method: 'PUT', headers, body: JSON.stringify(patch) });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && j.success !== false) { showNotification('Les rappels d\'entretien arrivent côté serveur.', 'error'); return; }
      // Le message du serveur fait foi — c'est lui qui borne le délai et
      // refuse un modèle inexistant.
      if (!r.ok || j.success === false) { showNotification(j.error || `HTTP ${r.status}`, 'error'); return; }
      setConf(j.data);
      showNotification(j.message || 'Réglage enregistré.', 'success');
    } catch (e) { showNotification(`Enregistrement impossible : ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  if (absent) return <p className="text-sm" style={{ color: 'var(--erp-text-tertiary)' }}>Les rappels d'entretien arrivent côté serveur.</p>;
  if (!conf) return <p className="text-sm" style={{ color: 'var(--erp-text-tertiary)' }}>Chargement…</p>;

  const ligne = (titre, aide, actif, onChange) => (
    <label className="erp-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '11px 13px', cursor: busy ? 'default' : 'pointer' }}>
      <input type="checkbox" checked={actif} disabled={busy} onChange={e => onChange(e.target.checked)} style={{ marginTop: '2px' }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: 'var(--erp-ink-strong)' }}>{titre}</span>
        <span style={{ display: 'block', fontSize: '11.5px', lineHeight: 1.5, color: 'var(--erp-text-tertiary)' }}>{aide}</span>
      </span>
    </label>
  );

  const STATUT = { envoye: 'envoyé', echec: 'échec', ignore: 'ignoré', en_cours: 'en cours' };
  const aCorriger = (journal || []).filter(x => x.statut === 'ignore' || x.statut === 'echec');

  return (
    <div className="space-y-4" style={{ maxWidth: '640px' }}>
      <p className="text-sm" style={{ color: 'var(--erp-text-secondary)', lineHeight: 1.6 }}>
        Un entretien confirmé la semaine dernière est un entretien oublié le jour même.
        Le rappel part automatiquement avant chaque rendez-vous confirmé.
      </p>

      {ligne('Envoyer des rappels', 'Coupe tout d\'un coup — aucun rappel ne partira, ni au recruteur ni au candidat.',
        conf.actif, v => enregistrer({ actif: v }))}

      <div style={{ opacity: conf.actif ? 1 : 0.5, pointerEvents: conf.actif ? 'auto' : 'none' }} className="space-y-3">
        <div className="erp-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 13px' }}>
          <label htmlFor="rappel-h" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--erp-ink-strong)' }}>Combien de temps avant</label>
          <input id="rappel-h" className="erp-field" type="number" step="0.5" min={bornes.min} max={bornes.max}
            defaultValue={conf.heures_avant} disabled={busy} style={{ width: '90px' }}
            onBlur={e => { const v = Number(e.target.value); if (v !== conf.heures_avant) enregistrer({ heures_avant: v }); }} />
          <span style={{ fontSize: '12px', color: 'var(--erp-text-tertiary)' }}>heures ({bornes.min} à {bornes.max})</span>
        </div>

        {ligne('Prévenir le recruteur', 'La personne qui a organisé l\'entretien reçoit un rappel. Interne : rien ne sort de la maison.',
          conf.recruteur, v => enregistrer({ recruteur: v }))}

        {ligne('Prévenir le candidat', 'Le candidat reçoit un rappel de son rendez-vous. C\'est un email sortant : il ne part jamais vers une fiche marquée « ne pas contacter ».',
          conf.candidat, v => enregistrer({ candidat: v }))}

        <div className="erp-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 13px', opacity: conf.candidat ? 1 : 0.5 }}>
          <label htmlFor="rappel-tpl" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--erp-ink-strong)', whiteSpace: 'nowrap' }}>Modèle du candidat</label>
          <select id="rappel-tpl" className="erp-field" disabled={busy || !conf.candidat} value={conf.template_id ?? ''}
            onChange={e => enregistrer({ template_id: e.target.value === '' ? null : Number(e.target.value) })} style={{ flex: 1, minWidth: 0 }}>
            <option value="">Texte par défaut (date, durée, adresse, contact)</option>
            {modeles.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
          </select>
        </div>
      </div>

      {aCorriger.length > 0 && (
        <div className="erp-card" style={{ padding: '12px 14px' }}>
          <div style={{ fontWeight: 700, fontSize: '12.5px', color: 'var(--erp-ink-strong)', marginBottom: '6px' }}>
            Rappels qui ne sont pas partis
          </div>
          <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', lineHeight: 1.6, color: 'var(--erp-text-secondary)' }}>
            {aCorriger.slice(0, 8).map(x => (
              <li key={x.id}><strong>{x.candidat}</strong> — {STATUT[x.statut] || x.statut} : {x.erreur || 'raison inconnue'}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
